import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Truck, Scan, Phone, CheckCircle2, Users, Building, Shield, Eye, Globe, Boxes, GraduationCap, Receipt, Camera, Languages, BarChart3, Smartphone, ChevronDown, Search, User, X, Headphones, Leaf, Sparkles, Heart, Rocket, Handshake, Recycle, Wrench, Layers, Network, ShieldCheck, ClipboardCheck, Award, Trophy, Building2, MapPin, ArrowRight, Droplets, Wind, Trees } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import NECSLogo, { NECSBrandText } from '../components/NECSLogo';
import AppStoreBadges from '../components/AppStoreBadges';
import LeadCaptureModal from '../components/LeadCaptureModal';
import MultifamilyQuoteForm from '../components/MultifamilyQuoteForm';
import FacilityAssessmentForm from '../components/FacilityAssessmentForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import CommercialQuoteEngine from '../components/CommercialQuoteEngine';
import SustainabilityImpactStrip from '../components/SustainabilityImpactStrip';
import '../styles/sustainability-page.css';
import '../styles/management-page.css';
import '../styles/about-page.css';
import '../styles/safety-page.css';
import ServicesRunningCarousel from '../components/ServicesRunningCarousel';
import { COMMERCIAL_SERVICE_SLIDES } from '../lib/commercialServiceSlides';
import { INDUSTRIES } from '../lib/industryData';

const packages = [
  {
    name: "Essential Collection",
    price: "Contact us for a custom quote",
    designedFor: "Reliable valet trash & recycling support, QR-coded containers, resident app, operational reporting, and common-area support for conventional apartment communities.",
    features: [
      "Single Valet Trash & Recycling Support (Monday to Friday)",
      "QR-coded trash containers",
      "NECS Resident App",
      "Operational reporting",
      "Common-area trash support",
    ],
    recommended: false
  },
  {
    name: "Professional Collection",
    price: "Contact us for a custom quote",
    designedFor: "Everything in Essential, plus enhanced operational monitoring, bi-annual dumpster treatment, and expanded management reporting.",
    features: [
      "Everything in Essential Collection",
      "Enhanced operational monitoring",
      "Dumpster treatment (bi-annual)",
      "Expanded management reporting",
    ],
    recommended: false
  },
  {
    name: "Elite Collection",
    price: "Contact us for a custom quote",
    designedFor: "Everything in Professional, plus daily property trash support, expanded cleanliness services, and enhanced community presentation.",
    features: [
      "Everything in Professional Collection",
      "Property Appearance Patrol",
      "Expanded cleanliness services",
      "Enhanced community presentation",
    ],
    recommended: false
  },
  {
    name: "Signature Collection",
    price: "Contact us for a custom quote",
    designedFor: "Everything in Elite, plus annual pressure washing, monthly bulk removal support, overflow dumpster management, priority support, and additional exterior property maintenance.",
    features: [
      "Everything in Elite Collection",
      "Annual pressure washing",
      "Monthly bulk removal support",
      "Overflow dumpster management",
      "Priority support",
      "Additional exterior property maintenance",
    ],
    recommended: true
  },
  {
    name: "Noble Empire Executive Collection",
    price: "Contact us for a custom quote",
    designedFor: "Double-shift valet trash & recycling, Sunday–Friday service, quarterly dumpster treatment, overflow dumpster management, annual pressure washing, bi-weekly bulk removal, Property Appearance Patrol, premium reporting, premium priority support, and professional interior & exterior window cleaning twice annually (biannual) for leasing offices and designated common areas.",
    features: [
      "Double Valet Trash & Recycling Support (Sunday to Friday)",
      "Quarterly dumpster treatment",
      "Overflow dumpster management",
      "Annual pressure washing",
      "Bi-weekly bulk removal",
      "Property Appearance Patrol",
      "Premium priority support",
      "Professional Interior & Exterior Window Cleaning — twice annually (biannual)",
    ],
    recommended: false
  },
  {
    name: "Noble Empire Presidential Collection",
    price: "Contact us for a custom quote",
    designedFor: "Flagship property support — everything in the Executive Collection, plus weekly eco-friendly amenity cleaning, clubhouse and fitness center cleaning, leasing office sanitization, VIP customer support, expanded luxury property services, sustainability consulting support, and professional interior & exterior window cleaning every quarter (four times annually) for leasing offices and designated common areas.",
    features: [
      "Everything in the Executive Collection",
      "Weekly eco-friendly amenity cleaning",
      "Clubhouse & fitness center cleaning",
      "Leasing office sanitization",
      "VIP customer support",
      "Expanded luxury property services",
      "Sustainability consulting support",
      "Professional Interior & Exterior Window Cleaning — every quarter (four times annually)",
    ],
    recommended: false
  }
];

const serviceVisuals = [
  {
    title: "Valet Trash and Recycling Collection",
    description: "NECS employees maintain professional appearance standards and operate according to company safety procedures and property policies.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/ufy62tfj_1927.png",
    alt: "Noble Empire NECS employee performing nightly valet trash and recycling collection at a resident's door",
    objectPosition: 'center 20%'
  },
  {
    title: "Professional Cleaning Teams",
    description: "Uniformed staff delivering consistent, high-quality results.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/v96zv1ow_97895.png",
    alt: "Noble Empire professional cleaning crew — faces and uniforms",
    objectPosition: 'center 25%'
  },
  {
    title: "Property Maintenance Support",
    description: "On-site crews and equipment ready for property needs.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/i26cozy3_91787.png",
    alt: "Noble Empire crew at a commercial property — branded van, pickup truck, vacuum, cleaning cart and full team",
    fit: 'contain'
  }
];

const highlightSlides = [
  {
    title: "Valet Trash & Recycling Collection",
    description: "Doorstep pickup with professional, on-time service by uniformed NECS staff.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/69t216o6_1623.jpg",
    alt: "NECS employee performing nightly valet trash and recycling collection at a resident's door with the resident smiling in the doorway",
    objectPosition: 'center 30%'
  },
  {
    title: "Commercial Cleaning",
    description: "Complete office business cleaning site, lobbies, amenities and common areas — pristine every visit.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/7w7zt9ib_90872.png",
    alt: "Noble Empire crew mopping a polished commercial lobby floor"
  },
  {
    title: "Pressure Washing Service",
    description: "Exterior pressure washing for sidewalks, walkways, and common areas.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/egsed6f4_91776.png",
    alt: "Noble Empire technician pressure-washing a commercial entrance"
  },
  {
    title: "Bulk Removal",
    description: "Scheduled bulk pickup for oversized items and cleanouts.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/8ggel5t8_92724.png",
    alt: "Noble Empire crew loading a sofa into a branded box truck"
  },
  {
    title: "Property Accommodation",
    description: "Around-property trash pickup — walkways, courtyards and common grounds kept spotless.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/fqpw6qbx_91777.png",
    alt: "Noble Empire technician picking up loose trash along a property walkway"
  },
  {
    title: "Dumpster Treatment",
    description: "Non-toxic sanitizing spray inside dumpsters with pressure washing around — fully cleaned and deodorized.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/o0yl93et_92743.png",
    alt: "Noble Empire agent pressure-washing and sanitizing a dumpster with non-toxic products"
  },
  {
    title: "Professional Interior & Exterior Window Cleaning",
    description: "Streak-free interior and exterior window cleaning for leasing offices, clubhouses, fitness centers, and designated common areas.",
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/rbff15xp_2567.png",
    alt: "NECS crew squeegeeing storefront and interior windows at a Dallas restaurant"
  }
];

const testimonials = [
  {
    quote: "Our nightly pickups are consistent and reliable. Residents notice the difference.",
    name: "Danielle M.",
    title: "Property Manager, Uptown Dallas",
    initials: "DM"
  },
  {
    quote: "Bulk removal and power wash schedules are handled with professionalism and care.",
    name: "Carlos R.",
    title: "Operations Director, Trinity Heights",
    initials: "CR"
  },
  {
    quote: "The team app keeps us updated in real-time, which makes reporting seamless.",
    name: "Jasmine P.",
    title: "Community Manager, Legacy Park",
    initials: "JP"
  }
];

const appInActionSlides = [
  {
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/n3d7bzfd_2094.png",
    alt: "NECS Resident App and NECS Staff App tiles — Get it on Google Play and Download on the App Store",
    fit: 'contain'
  },
  {
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/zvlu0ogg_94990.png",
    alt: "Two NECS team members in branded polos and caps holding phones showing the NECS Staff App"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/5sm0kiwn_94989.png",
    alt: "NECS team member greeting a resident at door 203 — both holding phones with the NECS Staff and Resident apps"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/vn61cz2j_2079.jpg",
    alt: "NECS Staff App and NECS Resident App brand identity poster — Noble Empire Cleaning Services laurel logo stacked over the two app titles",
    fit: 'contain'
  }
];

const heroSlides = [
  {
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/x9splok0_91987.png",
    alt: "Noble Empire serviced apartment community at dusk — manicured gardens, lit walkways, leasing office"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/xvo91vso_100647.png",
    alt: "Modern glass-fronted apartment building serviced by Noble Empire — illuminated balconies along a lit waterfront promenade"
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [showAllPackages, setShowAllPackages] = useState(false);
  // S6 — quote forms hidden until a Get a Quote button is clicked.
  const [quotePreselect, setQuotePreselect] = useState('');
  const [showMfQuote, setShowMfQuote] = useState(false);
  // Iter-152 · In-page modals for the final-CTA Quote + Assessment buttons.
  const [ctaModal, setCtaModal] = useState(null); // 'quote' | 'assessment' | null
  const [showCommQuote, setShowCommQuote] = useState(false);
  const [activeHighlight, setActiveHighlight] = useState(0);
  const [activeAppAction, setActiveAppAction] = useState(0);
  const [activeView, setActiveView] = useState('home');
  // S14 — restore a specific view when arriving with /?view= (e.g. industry page back links)
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get('view');
    if (v) {
      setActiveView(v);
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // E28 — per-view SEO metadata
  useEffect(() => {
    const SEO = {
      home: ['Noble Empire Cleaning Services — NECS Integrated Facility Care', 'One facility. One service partner. One integrated solution. Valet trash, commercial cleaning and facility care across DFW.'],
      multifamily: ['Multifamily Facility Care — NECS', 'Integrated property care for modern multifamily communities: valet trash, recycling, bulk removal, appearance patrol, pressure washing and amenity cleaning.'],
      commercials: ['Commercial Facility Care — NECS', 'Industry-specific facility-care programs for offices, retail, medical, industrial and more — one coordinated service relationship.'],
      sustainability: ['Sustainability — NECS', 'Cleaner properties. Responsible practices. A smarter approach to facility care.'],
      residents: ['Residents — NECS', 'Resident valet trash service, pickup schedules, bulk items and the NECS Resident App.'],
      management: ['Management — NECS', 'More visibility. More accountability. Less operational stress for property and facility managers.'],
      mission: ['Safety & Risk Management — NECS', 'OSHA-aligned safety practices across every NECS service line.'],
      about: ['About NECS — Integrated Facility Care', 'A technology-driven integrated facility care company serving multifamily and commercial properties.'],
      'service-packages': ['Service Packages — NECS', 'Six multifamily packages with preliminary estimate ranges by contract term.'],
      'technology-apps': ['Technology Platform — NECS', 'Resident App, Staff App, QR tracking, GPS and digital service verification.'],
    };
    const [title, desc] = SEO[activeView] || SEO.home;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = desc;
  }, [activeView]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeHero, setActiveHero] = useState(0);
  const [faqTab, setFaqTab] = useState('general');
  // Dynamic FAQ / services from the centralized company_knowledge collection.
  // Kept null until fetched — if the request fails we fall back to the
  // hardcoded FAQ_GROUPS below so the landing page never renders empty.
  const [knowledge, setKnowledge] = useState(null);
  useEffect(() => {
    let alive = true;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    fetch(`${backend}/api/knowledge`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (alive && data) setKnowledge(data); })
      .catch(() => { /* silent fallback to static content */ });
    return () => { alive = false; };
  }, []);
  const [navHidden, setNavHidden] = useState(false);
  const [residentsSlide, setResidentsSlide] = useState(0);
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const residentsCarousel = [
    {
      image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/a20zj90n_1924.png",
      alt: "Resident placing a tied trash bag into her NECS-branded bin at home — kids in living room behind her",
      objectPosition: 'center 30%'
    },
    {
      image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/wltfgc8p_1925.png",
      alt: "Smiling senior resident bringing his NECS-branded trash bin out of his apartment — wife knitting on the couch behind him",
      objectPosition: 'center 25%'
    },
    {
      image: "https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/vg3n6xc4_1623.jpg",
      alt: "Uniformed NECS employee collecting valet trash at a resident's door while the smiling resident greets her",
      objectPosition: 'center 25%'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setResidentsSlide((prev) => (prev + 1) % residentsCarousel.length);
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  // Hide top nav on scroll-down, reveal on scroll-up
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const currentY = window.scrollY;
      // Always show near the top of the page
      if (currentY < 80) {
        setNavHidden(false);
      } else if (currentY > lastY + 6) {
        setNavHidden(true);   // scrolling down
      } else if (currentY < lastY - 6) {
        setNavHidden(false);  // scrolling up
      }
      lastY = currentY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveHero((prev) => (prev + 1) % heroSlides.length);
    }, 6500);
    return () => clearInterval(interval);
  }, []);

  const goToView = (view) => {
    setActiveView(view);
    setMobileMenuOpen(false);
    // Iter-155 · keep URL query in sync with activeView so links can
    // deep-link + browser back/forward works.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('view', view);
      window.history.pushState({}, '', url.toString());
    } catch (e) { /* no-op */ }
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveHighlight((prev) => (prev + 1) % highlightSlides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveAppAction((prev) => (prev + 1) % appInActionSlides.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [activeAppAction]);

  const handleHighlightPrev = () => {
    setActiveHighlight((prev) => (prev - 1 + highlightSlides.length) % highlightSlides.length);
  };

  const handleHighlightNext = () => {
    setActiveHighlight((prev) => (prev + 1) % highlightSlides.length);
  };

  const displayedPackages = showAllPackages ? packages : packages.slice(0, 3);

  // Search dropdown surfaces the 4 services from the Contact page "Nature of Inquiry".
  // Clicking one routes to /contact?source=pricing&service=<name> so the form pre-selects it.
  const SEARCH_TARGETS = [
    { label: 'Valet Trash & Recycling Separation' },
    { label: 'Bulk Removal' },
    { label: 'Pressure Washing' },
    { label: 'Professional Interior & Exterior Window Cleaning' },
    { label: 'Commercial Cleaning (Eco-Friendly)' },
  ];
  const searchMatches = searchQuery.trim().length === 0
    ? SEARCH_TARGETS
    : SEARCH_TARGETS.filter((t) => t.label.toLowerCase().includes(searchQuery.trim().toLowerCase()));

  const onPickSearchResult = (t) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/contact?source=pricing&service=${encodeURIComponent(t.label)}`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#3D3D3D' }} data-active-view={activeView}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full backdrop-blur-sm z-50 transition-transform duration-300 ease-in-out" style={{ backgroundColor: '#C9A962', transform: navHidden ? 'translateY(-100%)' : 'translateY(0)' }} data-testid="landing-top-nav" data-nav-hidden={navHidden}>
        <div className="flex items-center h-20 sm:h-24 pr-4 sm:pr-6 lg:pr-8">
          <div className="flex items-center h-full pl-0" style={{ backgroundColor: '#2D2D2D' }}>
            <NECSLogo variant="header" size="sm" dataTestId="landing-page-logo" align="left" className="h-full" />
          </div>
            {/* Desktop nav — centered within the gold bar */}
            <div className="hidden md:flex flex-1 justify-center items-center space-x-1">
              {[
                // Iter-149 · Section 3 — canonical primary navigation.
                // HOME | MULTIFAMILY | SUSTAINABILITY | RESIDENTS |
                // MANAGEMENT | SAFETY | COMMERCIAL SERVICES | ABOUT NECS.
                // "Services" and "Service Packages" are no longer top-level
                // categories per spec; their content lives under Multifamily
                // and Commercial Services.
                { label: 'Home', view: 'home' },
                { label: 'Multifamily', view: 'multifamily' },
                { label: 'Sustainability', view: 'sustainability' },
                { label: 'Residents', view: 'residents' },
                { label: 'Management', view: 'management' },
                { label: 'Safety', view: 'mission' },
                { label: 'Commercial Services', view: 'commercials' },
                { label: 'About NECS', view: 'about' },
              ].map((item) => (
                <div key={item.view} className="relative group">
                  <button
                    onClick={() => (item.route ? navigate(item.route) : goToView(item.view))}
                    className={`px-3 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${activeView === item.view || (item.children && item.children.some(c => c.view === activeView)) ? 'text-black' : 'text-white hover:text-black'}`}
                    style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.15em' }}
                    data-testid={`nav-${item.view}`}
                    data-active={activeView === item.view}
                  >
                    {item.label}
                  </button>
                  {item.children && (
                    <div
                      className="absolute left-0 top-full mt-0 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 z-50 w-[200px]"
                      style={{ backgroundColor: '#2D2D2D', border: '1px solid #C9A962' }}
                      data-testid={`nav-${item.view}-submenu`}
                    >
                      {item.children.map((child) => (
                        <button
                          key={child.view}
                          onClick={() => goToView(child.view)}
                          className={`block w-full text-left px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${activeView === child.view ? 'text-[#C9A962]' : 'text-white hover:text-[#C9A962]'}`}
                          style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.1em' }}
                          data-testid={`nav-${child.view}`}
                          data-active={activeView === child.view}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right cluster — search + login icons */}
            <div className="hidden md:flex items-center gap-3 relative">
              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                className="flex items-center justify-center w-10 h-10 rounded-full text-white hover:text-black hover:bg-white/15 transition"
                aria-label="Open search"
                aria-expanded={searchOpen}
                data-testid="nav-search-trigger"
              >
                <Search className="w-5 h-5" strokeWidth={2.4} />
              </button>
              <button
                type="button"
                onClick={() => navigate('/live-demo')}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-full bg-white text-black hover:bg-zinc-100 transition shadow-lg border border-[#C9A962]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.12em' }}
                data-testid="nav-try-demo-btn"
                title="Spin up a private NECS demo in 30 seconds"
              >
                <Rocket className="w-4 h-4" />
                <span>Try Live Demo</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-3 py-2 text-sm font-bold uppercase tracking-wider text-white hover:text-black transition"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.15em' }}
                data-testid="nav-login"
                title="Sign in to your dashboard"
              >
                <User className="w-5 h-5" strokeWidth={2} />
                <span>Log In</span>
              </button>

              {searchOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                    data-testid="nav-search-backdrop"
                  />
                  <div
                    role="dialog"
                    aria-label="Search the site"
                    className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-2xl border-2 overflow-hidden z-50"
                    style={{ backgroundColor: '#000000', borderColor: '#C9A962' }}
                    data-testid="nav-search-panel"
                  >
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                      <Search className="w-4 h-4 text-[#C9A962]" />
                      <input
                        type="text"
                        autoFocus
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search services…"
                        className="flex-1 bg-transparent text-white text-sm placeholder:text-zinc-500 focus:outline-none"
                        data-testid="nav-search-input"
                      />
                      <button
                        type="button"
                        onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                        className="text-zinc-400 hover:text-white"
                        aria-label="Close search"
                        data-testid="nav-search-close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#C9A962] border-b border-white/5">
                      Nature of Inquiry
                    </div>
                    <ul className="max-h-72 overflow-y-auto" data-testid="nav-search-results">
                      {searchMatches.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-zinc-400">No matches.</li>
                      ) : (
                        searchMatches.map((t) => (
                          <li key={t.label}>
                            <button
                              type="button"
                              onClick={() => onPickSearchResult(t)}
                              className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white hover:bg-[#1a1a1a] transition border-b border-white/5 last:border-b-0"
                              data-testid={`nav-search-result-${t.label.toLowerCase().split(' ')[0]}`}
                            >
                              <span className="inline-block w-2 h-2 rounded-full bg-[#C9A962] flex-shrink-0" />
                              <span>{t.label}</span>
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </>
              )}
            </div>
            {/* Mobile nav — hamburger trigger + dropdown menu */}
            <div className="md:hidden ml-auto relative">
              <button
                type="button"
                onClick={() => setMobileMenuOpen((o) => !o)}
                className="flex items-center justify-center w-11 h-11 hover:opacity-80 transition"
                data-testid="mobile-nav-trigger"
                aria-expanded={mobileMenuOpen}
                aria-haspopup="menu"
                aria-label="Open menu"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <line x1="4" y1="6"  x2="20" y2="6"  />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>

              {mobileMenuOpen && (
                <>
                  {/* tap-outside backdrop */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-backdrop"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-64 rounded-xl shadow-2xl border-2 overflow-hidden z-50"
                    style={{ backgroundColor: '#000000', borderColor: '#C9A962' }}
                    data-testid="mobile-nav-dropdown"
                  >
                    {[
                      { label: 'Home', value: 'home', type: 'view' },
                      { label: 'Safety', value: 'mission', type: 'view' },
                      { label: 'Sustainability', value: 'sustainability', type: 'view' },
                      { label: 'Services', value: 'services', type: 'view' },
                      { label: 'Technology & Apps', value: 'technology-apps', type: 'view' },
                      { label: 'Multifamily', value: 'multifamily', type: 'view' },
                      { label: 'Residents', value: 'residents', type: 'view' },
                      { label: 'COMMERCIAL SERVICES', value: 'commercials', type: 'view' },
                      { label: 'Management', value: 'management', type: 'view' },
                      { label: 'Log In', value: 'login', type: 'login' },
                    ].map((item) => {
                      const isActive = item.type === 'view' && activeView === item.value;
                      return (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => {
                            if (item.type === 'login') {
                              setMobileMenuOpen(false);
                              navigate('/login');
                            } else if (item.type === 'route') {
                              setMobileMenuOpen(false);
                              navigate(item.value);
                            } else {
                              goToView(item.value);
                            }
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-white hover:bg-[#1a1a1a] transition border-b border-white/10 last:border-b-0"
                          style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em' }}
                          data-testid={`mobile-nav-item-${item.value}`}
                          role="menuitem"
                        >
                          <span
                            className="inline-block rounded-full flex-shrink-0"
                            style={{
                              width: '10px',
                              height: '10px',
                              backgroundColor: '#C9A962',
                              boxShadow: isActive ? '0 0 0 3px rgba(201,169,98,0.35)' : 'none',
                            }}
                            aria-hidden="true"
                          />
                          <span className={isActive ? 'text-[#C9A962]' : 'text-white'}>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
      </nav>

      {/* Hero Carousel + Mission overlay — pictures keep rotating in the
          carousel; the "OUR MISSION" copy sits directly over the imagery
          (no card / no box), matching the cinematic look used on the
          Safety / Services / Management / About NECS menu pages. */}
      <section
        className="pt-20 sm:pt-24 px-0 text-white relative overflow-hidden"
        style={{ backgroundColor: '#2D2D2D' }}
        data-testid="landing-hero-property-image"
        data-views="home"
      >
        <div className="relative w-full aspect-[16/9] sm:aspect-[21/9] bg-[#1a1a1a]" data-testid="hero-carousel">
          {heroSlides.map((slide, index) => (
            <img
              key={slide.image}
              src={slide.image}
              alt={slide.alt}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : 'low'}
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${index === activeHero ? 'opacity-100' : 'opacity-0'}`}
              data-testid={`hero-slide-${index}`}
            />
          ))}

          {/* Soft bottom gradient — keeps the mission copy readable against
              whichever hero slide is active (no box, just darkening). */}
          <div
            className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0.9) 100%)' }}
            aria-hidden="true"
          />

          {/* OUR MISSION — text-only overlay sitting on the carousel itself */}
          <div
            key={`mission-${activeHero}`}
            className="absolute inset-x-0 bottom-10 sm:bottom-16 z-10 px-6 sm:px-10 lg:px-16 necs-mission-rise"
            data-views="home"
            data-testid="home-mission-block"
          >
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center tracking-tight text-white"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                textTransform: 'uppercase',
                textShadow: '0 2px 12px rgba(0,0,0,0.85)',
              }}
            >
              OUR <span style={{ color: '#C9A962' }}>MISSION</span>
            </h2>
            <div
              className="mx-auto mt-2 mb-4"
              style={{ width: '64px', height: '2px', backgroundColor: '#C9A962', boxShadow: '0 2px 8px rgba(0,0,0,0.6)' }}
              aria-hidden="true"
            />
            <p
              className="text-center text-base sm:text-lg max-w-4xl mx-auto"
              style={{
                color: '#f5f5f5',
                lineHeight: 1.7,
                textShadow: '0 2px 10px rgba(0,0,0,0.85)',
              }}
            >
              Our mission is to help property owners and management teams improve operational efficiency, elevate resident and customer experiences, reduce maintenance challenges, and protect long-term property value through dependable, innovative, and sustainability-focused service solutions.
            </p>
          </div>

          {/* Indicator dots — kept on top, near the very bottom of the carousel */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="hero-dots">
            {heroSlides.map((_, index) => (
              <button
                key={`hero-dot-${index}`}
                type="button"
                aria-label={`Slide ${index + 1}`}
                onClick={() => setActiveHero(index)}
                className={`h-2.5 w-2.5 rounded-full transition border border-black/30 ${index === activeHero ? 'bg-[#C9A962]' : 'bg-white/60'}`}
                data-testid={`hero-dot-${index}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Safety: original background-only layout, using the upper-body crop with both boards retained. */}
      <div
        className="safety-page-background pt-24 sm:pt-28"
        style={{ '--safety-background-image': 'url("/images/safety-team-training-chest-crop.webp")' }}
        data-views="mission"
        data-testid="safety-shared-bg-wrapper"
      >
      {/* Safety Section — Iter-152 · full text replacement per user spec. */}
      <section
        className="relative py-16 px-4 sm:px-6 lg:px-8"
        data-views="mission"
        data-testid="safety-hero"
        id="safety"
      >
        <div className="relative max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#7CFC8A' }}>NECS Safety</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }} data-testid="safety-page-heading">
            SAFETY WITHOUT <span style={{ color: '#C9A962' }}>COMPROMISE</span>
          </h2>
          <p
            className="text-lg font-semibold mb-6"
            style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}
            data-testid="safety-subtitle"
          >
            Protecting People. Protecting Property. Protecting the Environment.
          </p>
          <p className="text-base sm:text-lg max-w-4xl mx-auto mb-4" style={{ color: '#e5e5e5', lineHeight: 1.75 }}>
            At Noble Empire Cleaning Services, safety is built into the way we plan, perform, verify, and improve every service. Our goal is not simply to make a property look clean — it is to deliver professional results while reducing unnecessary risk to residents, employees, customers, pets, property, and the surrounding environment.
          </p>
          <p className="text-base sm:text-lg max-w-4xl mx-auto" style={{ color: '#e5e5e5', lineHeight: 1.75 }}>
            NECS selects products, equipment, procedures, and service methods based on the requirements of each facility and the specific application. Where operationally appropriate, we prioritize environmentally responsible products and water-conscious practices without compromising cleaning performance, sanitation requirements, or worker safety.
          </p>
        </div>
      </section>

      {/* Safety Standards + Our Standard grid — Iter-152 · full replacement. */}
      <section
        id="our-commitment"
        className="relative py-12 px-4 sm:px-6 lg:px-8"
        data-testid="our-commitment-section"
        data-views="mission"
      >
        <div className="relative max-w-7xl mx-auto">
          <h3 className="text-xl sm:text-2xl font-bold text-center mb-8 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            Our Safety <span style={{ color: '#C9A962' }}>Standards Include</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-12" data-testid="safety-standards-grid">
            {[
              { title: 'People First',
                body: 'We use documented procedures, appropriate training, and job-specific protective measures to help protect residents, customers, employees, and service partners.' },
              { title: 'Responsible Product Selection',
                body: 'We prioritize effective products with responsible environmental profiles when they are appropriate for the surface, application, facility, and required level of sanitation.' },
              { title: 'Chemical Awareness & Control',
                body: 'Cleaning and treatment products are handled according to manufacturer instructions, applicable requirements, and site-specific procedures. Safety Data Sheets (SDS) are maintained for applicable products used by our teams.' },
              { title: 'Water-Conscious Operations',
                body: 'For exterior cleaning and pressure-washing services, NECS uses equipment and operating practices designed to manage water consumption and minimize unnecessary waste while achieving the required cleaning result.' },
              { title: 'Property & Surface Protection',
                body: 'Our teams consider surface type, landscaping, drainage, surrounding materials, pedestrian traffic, and operating conditions before performing specialized cleaning or exterior services.' },
              { title: 'Controlled Service Areas',
                body: 'When a service creates temporary hazards — such as wet floors, active pressure washing, cleaning operations, or chemical application — appropriate precautions and communication are used to help protect people and property.' },
              { title: 'Continuous Safety Improvement',
                body: 'Safety is an ongoing operational discipline. NECS continuously evaluates procedures, equipment, training, products, and field practices to improve service quality and reduce preventable risk.' },
            ].map((s) => (
              <div
                key={s.title}
                className="rounded-2xl p-5 border"
                style={{ backgroundColor: 'rgba(45,45,45,0.85)', borderColor: 'rgba(124,252,138,0.3)' }}
                data-testid={`safety-standard-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <p className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: '#7CFC8A', fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {s.title}
                </p>
                <p className="text-sm" style={{ color: '#d6d6d6', lineHeight: 1.7 }}>{s.body}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl sm:text-2xl font-bold text-center mb-8 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            Our <span style={{ color: '#C9A962' }}>Standard</span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="our-standard-grid">
            {[
              { title: 'Safe by Design',
                body: 'Safety considerations are incorporated into service planning and field procedures.' },
              { title: 'Responsible by Practice',
                body: 'Environmental considerations are evaluated alongside performance and operational requirements.' },
              { title: 'Professional by Process',
                body: 'Our teams follow documented procedures appropriate to the service being performed.' },
              { title: 'Accountable by Verification',
                body: 'Service activities can be documented, monitored, and reviewed through NECS operational systems.' },
            ].map((t) => (
              <div
                key={t.title}
                className="rounded-2xl p-5 border text-center"
                style={{ backgroundColor: 'rgba(45,45,45,0.85)', borderColor: 'rgba(201,169,98,0.4)' }}
                data-testid={`our-standard-${t.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <p className="text-xs font-black uppercase tracking-[0.24em]" style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {t.title}
                </p>
                <p className="text-xs mt-3" style={{ color: '#d6d6d6', lineHeight: 1.7 }}>{t.body}</p>
              </div>
            ))}
          </div>

          <p
            className="text-center text-lg font-semibold max-w-3xl mx-auto"
            style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}
            data-testid="safety-closing-line"
          >
            Safety is not an add-on to our service. It is part of the service.
          </p>
        </div>
      </section>
      </div>
      {/* /Safety menu shared-background wrapper */}

      {/* Home Service Visuals — original 3-card image grid shown only on Home */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} data-views="home service-packages" data-testid="home-service-visuals-cards">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 tracking-tight text-white" style={{fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase'}}>
            SERVICE <span style={{ color: '#C9A962' }}>VISUALS</span>
          </h2>
          <p className="text-center text-lg max-w-4xl mx-auto mb-12" style={{ color: '#a0a0a0' }}>
            Professional on-site service, trained teams, and dependable nightly pickup.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {serviceVisuals.map((visual, index) => (
              <Card
                key={visual.title}
                className="overflow-hidden transition-all hover:scale-[1.02]"
                style={{ backgroundColor: '#2D2D2D', border: '2px solid #4a4a4a' }}
                data-testid={`home-service-visual-card-${index}`}
              >
                <img
                  src={visual.image}
                  alt={visual.alt}
                  className={`w-full h-48 ${visual.fit === 'contain' ? 'object-contain bg-zinc-900' : 'object-cover'}`}
                  style={visual.objectPosition ? { objectPosition: visual.objectPosition } : undefined}
                  data-testid={`home-service-visual-image-${index}`}
                />
                <div className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                    {visual.title}
                  </h3>
                  <p className="text-sm" style={{ color: '#a0a0a0' }}>
                    {visual.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Why Choose Noble Empire — shown only on Home (S25: NECS Difference content transferred here) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-views="home" data-testid="home-why-choose-block">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-4 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            The <span style={{ color: '#C9A962' }}>NECS</span> Difference
          </h3>
          <p className="text-center text-base max-w-4xl mx-auto mb-10" style={{ color: '#a0a0a0', lineHeight: 1.7 }}>
            Fewer handoffs. Clearer accountability. NECS brings your property’s recurring needs into one coordinated program, with service records your team can review.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Integrated Services', desc: 'One facility. One service partner. One integrated solution.' },
              { title: 'Technology-Driven Accountability', desc: 'QR tracking, GPS and digital verification on every visit.' },
              { title: 'Sustainability-Focused Operations', desc: 'Responsible products and practices where requirements allow.' },
              { title: 'Professional Service Teams', desc: 'Badge-verified, background-checked, training-tracked crews.' },
              { title: 'Property-Specific Programs', desc: "Programs built around each property's operational needs." },
              { title: 'Digital Reporting', desc: 'Operational reports and dashboards for management teams.' },
              { title: 'Responsive Customer Support', desc: 'Live support with fast escalation to a real person.' },
              { title: 'Long-Term Partnership', desc: 'Structured agreements built for consistency and growth.' },
            ].map((s) => (
              <Card key={s.title} className="p-5" style={{ backgroundColor: '#3D3D3D', border: '2px solid #4a4a4a' }} data-testid={`difference-card-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <CheckCircle2 className="h-6 w-6 mb-3" style={{ color: '#C9A962' }} />
                <h3 className="text-base font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>{s.title}</h3>
                <p className="text-xs" style={{ color: '#a0a0a0', lineHeight: 1.6 }}>{s.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services menu: single shared background image spans both "Our Services" intro AND "Service Solutions" list — cover + fixed so the image fills the entire page and only text scrolls (matching Management / About NECS) */}
      <div
        className="relative pt-24 sm:pt-28"
        style={{
          backgroundColor: '#2D2D2D',
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/4tlacukg_2111.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
        }}
        data-views="services"
        data-testid="services-shared-bg-wrapper"
      >
        {/* dark scrim covering both sections so all white/gold text stays readable */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.82) 100%)' }}
          data-testid="services-shared-bg-scrim"
        />

      {/* Service Visuals Section — NECS services intro script (Services menu only) */}
      <section
        className="relative py-16 px-4 sm:px-6 lg:px-8"
        data-testid="service-visuals-section"
        data-views="services"
      >
        <div className="relative max-w-5xl mx-auto">
          {/* S10 — page header branding */}
          <div className="text-center mb-10" data-testid="services-ifc-header">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
              NECS INTEGRATED <span style={{ color: '#C9A962' }}>FACILITY CARE</span>
            </h1>
            <p className="text-base sm:text-lg mt-2 font-semibold" style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}>
              One Facility. One Service Partner. One Integrated Solution.
            </p>
          </div>
          <p className="text-xs uppercase tracking-[0.3em] mb-3 text-center" style={{ color: '#C9A962' }}>Our Services</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 tracking-tight text-white" style={{fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase'}}>
            Professional Property Support <span style={{ color: '#C9A962' }}>Solutions</span>
          </h2>
          <div className="space-y-5 text-lg" style={{ color: '#f5f5f5', lineHeight: 1.75 }}>
            <p>
              Cleaning, waste collection, floor care and exterior maintenance — coordinated around your property’s priorities.
            </p>
            <p>
              Explore the scope below, then choose your facility type for the relevant service program.
            </p>
          </div>
        </div>
      </section>

      {/* Service Solutions — 6 service blocks above the Service Highlights carousel (Services menu only) */}
      <section
        className="relative py-16 px-4 sm:px-6 lg:px-8"
        data-testid="service-solutions-section"
        data-views="services"
      >
        <div className="relative max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] mb-3 text-center" style={{ color: '#C9A962' }}>Service Solutions</p>

          <div className="space-y-10">
            {[
              ['Waste Management', 'Complete waste-stream coordination — collection, dumpsters and disposal handled on a schedule built around your property.'],
              ['Recycling', 'Doorstep and facility recycling programs that keep recyclables out of the waste stream and support your diversion goals.'],
              ['Janitorial Cleaning', 'Consistent, professional cleaning by uniformed, badge-verified NECS teams — every visit digitally verified.'],
              ['Sanitation', 'Scheduled sanitation rounds for restrooms, high-touch surfaces and shared areas that keep facilities healthy.'],
              ['Floor Care', 'Routine floor cleaning programs matched to every surface type, from lobbies to service corridors.'],
              ['Floor Polishing', 'Professional polishing that restores shine, protects surfaces and keeps floors consistently presentation-ready.'],
              ['Floor Restoration', 'Deep restoration programs that bring worn floors back to a professional standard and extend their life.'],
              ['Window Cleaning', 'Interior and exterior window programs that keep storefronts, offices and common areas crystal clear.'],
              ['Pressure Washing', 'Exterior pressure washing for walkways, entries, dumpster pads and parking areas — first impressions handled.'],
              ['Bulk Removal', 'Scheduled and on-demand removal of furniture, oversized items and cleanout debris, done discreetly and fast.'],
              ['Day Porter Services', 'On-site daytime coverage that keeps high-traffic facilities spotless while your business stays open.'],
              ['Property Appearance', 'Scheduled exterior inspections and removal of visible ordinary litter and debris from designated property areas.'],
              ['Sustainable Facility Programs', 'Responsible products, water-conscious methods and waste-reduction practices built into your service program.'],
              ['Specialty Facility Services', 'Deep cleaning, dumpster treatment, amenity cleaning and project work — quoted separately or added to any program.'],
            ].map(([name, desc]) => {
              const words = name.split(' ');
              const first = words.slice(0, Math.max(1, words.length - 1)).join(' ');
              const last = words.length > 1 ? words[words.length - 1] : '';
              return (
                <div key={name} data-testid={`service-solution-${name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
                    {first} {last && <span style={{ color: '#C9A962' }}>{last}</span>}
                  </h3>
                  <p className="text-lg" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
                    {desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      </div>
      {/* /Services menu shared-background wrapper */}

      {/* Service Highlights Carousel */}
      <section id="services" className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-testid="service-highlights-section" data-views="services">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 tracking-tight text-white" style={{fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase'}}>
              SERVICE <span style={{ color: '#C9A962' }}>HIGHLIGHTS</span>
            </h2>
            <p className="text-lg mb-6" style={{ color: '#a0a0a0' }}>
              A closer look at the work on site.
            </p>
            <div className="space-y-4">
              {highlightSlides.map((slide, index) => (
                <div
                  key={slide.title}
                  className={`flex items-start gap-3 ${index === activeHighlight ? 'opacity-100' : 'opacity-60'}`}
                  data-testid={`service-highlight-list-${index}`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full mt-2"
                    style={{ backgroundColor: index === activeHighlight ? '#C9A962' : '#6b6b6b' }}
                  />
                  <div>
                    <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                      {slide.title}
                    </h3>
                    <p className="text-sm" style={{ color: '#a0a0a0' }}>
                      {slide.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div
              className="relative h-80 sm:h-96 overflow-hidden rounded-xl border"
              style={{ borderColor: '#C9A962' }}
              data-testid="service-highlights-carousel"
            >
              {highlightSlides.map((slide, index) => (
                <div
                  key={slide.title}
                  className={`absolute inset-0 transition-opacity duration-700 ${index === activeHighlight ? 'opacity-100' : 'opacity-0'}`}
                  data-testid={`service-highlight-slide-${index}`}
                >
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className="w-full h-full object-cover"
                    style={slide.objectPosition ? { objectPosition: slide.objectPosition } : undefined}
                    data-testid={`service-highlight-image-${index}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-5 left-5">
                    <p className="text-xl font-bold text-white" data-testid={`service-highlight-title-${index}`}>
                      {slide.title}
                    </p>
                    <p className="text-sm text-white/80" data-testid={`service-highlight-description-${index}`}>
                      {slide.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleHighlightPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-white/40 bg-black/40 text-white flex items-center justify-center"
              data-testid="service-highlight-prev"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={handleHighlightNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-white/40 bg-black/40 text-white flex items-center justify-center"
              data-testid="service-highlight-next"
            >
              ›
            </button>

            <div className="flex justify-center gap-2 mt-4" data-testid="service-highlight-dots">
              {highlightSlides.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  type="button"
                  onClick={() => setActiveHighlight(index)}
                  className={`h-2.5 w-2.5 rounded-full ${index === activeHighlight ? 'bg-[#C9A962]' : 'bg-white/40'}`}
                  data-testid={`service-highlight-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Closing Statement — appears under Service Highlights carousel (Services menu only) */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-testid="services-closing-statement" data-views="services">
        <div className="max-w-5xl mx-auto space-y-5 text-lg text-center" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
          <p>
            Find the program that fits your site.
          </p>
          <p className="text-white font-semibold pt-2">
            <Link to="/?view=multifamily" data-testid="services-next-multifamily" className="text-[#C9A962] underline">Multifamily communities</Link> · <Link to="/?view=commercials" data-testid="services-next-commercial" className="text-[#C9A962] underline">Commercial facilities</Link>
          </p>
          <p>
            <Link to="/quote" data-testid="services-next-quote" className="text-[#C9A962] underline">Build your service program →</Link>
          </p>
        </div>
      </section>

      {/* Packages Section - No Prices */}
      {/* Service Packages — sub-page of Services dropdown */}
      {/* S4 — Multifamily intro (above all existing content) */}
      {/* Iter-151 · Phase 4a — Multifamily short intro cap removed. */}

      {/* ═══ MULTIFAMILY (E5/E6) — Integrated property care gateway ═══ */}
      <section className="pt-24 sm:pt-28 pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-testid="multifamily-hero-section" data-views="multifamily">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#C9A962' }}>NECS Integrated Facility Care</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            INTEGRATED PROPERTY CARE FOR MODERN <span style={{ color: '#C9A962' }}>MULTIFAMILY COMMUNITIES</span>
          </h1>
          <p className="text-base sm:text-lg max-w-4xl mx-auto mb-4" style={{ color: '#a0a0a0', lineHeight: 1.7 }}>
            Keep collection windows, shared amenities and move-out demands coordinated with daily life in your community. Your program reflects resident access, building layout and property rules.
          </p>
          <p className="text-lg font-semibold" style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}>
            One Community. Multiple Property Needs. One NECS Partner.
          </p>
          {/* Iter-151 · Phase 5a — "REQUEST A PROPERTY PROPOSAL" and
              "TALK TO NECS" CTAs removed per spec. */}
        </div>
      </section>

      {/* E6 — Multifamily service cards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} data-testid="multifamily-services-section" data-views="multifamily">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            MULTIFAMILY <span style={{ color: '#C9A962' }}>SERVICES</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {[
              { title: 'Valet Trash', desc: 'Doorstep collection with professional, on-time service by uniformed NECS staff.', Icon: Truck },
              { title: 'Recycling', desc: 'Doorstep recycling collection that keeps recyclables out of the waste stream.', Icon: Leaf },
              { title: 'Bulk Removal', desc: 'Scheduled bulk pickup for oversized items, move-outs and cleanouts.', Icon: Boxes },
              { title: 'Overflow Waste Support', desc: 'Overflow dumpster solutions when volume spikes beyond capacity.', Icon: Building },
              { title: 'Dumpster & Trash Room Care', desc: 'Dumpster treatment, deodorizing and trash room maintenance.', Icon: Shield },
              { title: 'Property Appearance Patrol', desc: 'Scheduled exterior inspections and removal of visible ordinary litter and debris from designated property areas.', Icon: Eye },
              { title: 'Pressure Washing', desc: 'Exterior pressure washing for sidewalks, breezeways and common areas.', Icon: Sparkles },
              { title: 'Window Cleaning', desc: 'Interior and exterior window cleaning for leasing offices and common areas.', Icon: Scan },
              { title: 'Floor Care', desc: 'Floor cleaning, polishing and restoration programs for interior surfaces.', Icon: BarChart3 },
              { title: 'Amenity Cleaning', desc: 'Leasing offices, clubhouses, fitness centers, business centers, mailrooms, pool areas, dog stations, laundry facilities and common areas.', Icon: Users },
            ].map((s) => (
              <Card key={s.title} className="p-5 transition-all hover:scale-105 flex flex-col" style={{ backgroundColor: '#2D2D2D', border: '2px solid #4a4a4a' }} data-testid={`mf-service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <s.Icon className="h-7 w-7 mb-3" style={{ color: '#C9A962' }} />
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>{s.title}</h3>
                <p className="text-xs" style={{ color: '#a0a0a0', lineHeight: 1.6 }}>{s.desc}</p>
              </Card>
            ))}
          </div>
          {/* Iter-151 · Phase 5b — Running services carousel directly
              under the Multifamily services grid. */}
          <ServicesRunningCarousel testId="multifamily-carousel" />
        </div>
      </section>

      {/* Iter-151 · Phase 4a — Sustainability short intro cap removed. */}

      {/* ═══ SUSTAINABILITY (E19) ═══ */}
      <div className="sustainability-page-background" style={{ '--sustainability-background-image': 'url("/images/sustainability-community-care.webp")' }} data-views="sustainability" data-testid="sustainability-page-background">
      <section className="pt-28 sm:pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="sustainability-hero-section" data-views="sustainability">
        <div className="sustainability-hero-copy max-w-7xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#7CFC8A' }} data-testid="sustainability-eyebrow">NECS Sustainability</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }} data-testid="sustainability-page-heading">
            CLEANER PROPERTIES. RESPONSIBLE PRACTICES. <span style={{ color: '#C9A962' }}>A SMARTER APPROACH.</span>
          </h1>
          <div className="max-w-4xl mx-auto text-left sm:text-center space-y-5 text-base sm:text-lg" style={{ color: '#e5e5e5', lineHeight: 1.75 }} data-testid="sustainability-hero-body">
            <p>
              At Noble Empire Cleaning Services (NECS), sustainability means delivering high-quality property care while making responsible choices for people, properties, and the environment. We prioritize environmentally responsible products, water-conscious practices, waste reduction, recycling, and efficient service methods whenever they are appropriate for the application and operational requirements.
            </p>
            <p>
              Our approach is practical and measurable: better resource management, responsible service practices, and cleaner environments&mdash;without compromising performance, safety, or property standards.
            </p>
            <p className="font-semibold" style={{ color: '#7CFC8A', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em', textTransform: 'uppercase' }} data-testid="sustainability-hero-tagline">
              Responsible Operations. Cleaner Properties. A Better Future.
            </p>
          </div>
        </div>
      </section>
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="sustainability-practices-section" data-views="sustainability">
        <div className="max-w-7xl mx-auto">
          {/* Availability only until verified operational metrics are connected. */}
          <SustainabilityImpactStrip />

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { title: 'Resident-Conscious Product Selection', desc: 'Products are selected and applied according to manufacturer instructions, safety requirements, and the needs of occupied environments.' },
              { title: 'Waste Reduction', desc: 'Programs designed to reduce waste volume at the property level.' },
              { title: 'Recycling', desc: 'Doorstep and facility recycling programs that support diversion goals.' },
              { title: 'Water-Conscious Practices', desc: 'Water-conscious equipment and methods on exterior cleaning work.' },
              { title: 'Efficient Equipment', desc: 'Modern, efficient equipment maintained for lower operational impact.' },
              { title: 'Reusable Systems', desc: 'Reusable containers, cans and service systems wherever practical.' },
              { title: 'Chemical Management', desc: 'Controlled storage, handling and application of all cleaning chemicals.' },
              { title: 'SDS Availability', desc: 'Safety Data Sheets maintained and available for products in use.' },
              { title: 'Employee Training', desc: 'Teams trained on responsible product use and safe handling.' },
              { title: 'Sustainability Reporting', desc: 'Program-level reporting available for management teams on request.' },
            ].map((s) => (
              <Card key={s.title} className="p-5" style={{ backgroundColor: '#2D2D2D', border: '2px solid #4a4a4a' }} data-testid={`sus-card-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <Leaf className="h-6 w-6 mb-3" style={{ color: '#7CFC8A' }} />
                <h3 className="text-base font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>{s.title}</h3>
                <p className="text-xs" style={{ color: '#a0a0a0', lineHeight: 1.6 }}>{s.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
      </div>

      {/* ═══ SAFETY & RISK MANAGEMENT (E21) — appended to Safety view ═══ */}
      {/* Iter-152 · NECS SAFETY AND RISK MANAGEMENT operational catalog
          removed per user request. */}

      {/* ═══ COMMERCIALS (S5) — mirrors the Multifamily page structure ═══ */}
      {/* Iter-152 · Removed short intro cap per user request. */}
      <section className="pt-28 sm:pt-32 pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-testid="commercials-hero-section" data-views="commercials">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#C9A962' }}>NECS Integrated Facility Care</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            INTEGRATED FACILITY CARE FOR MODERN <span style={{ color: '#C9A962' }}>COMMERCIAL ENVIRONMENTS</span>
          </h1>
          <p className="text-base sm:text-lg max-w-4xl mx-auto mb-4" style={{ color: '#a0a0a0', lineHeight: 1.7 }}>
            Service planning starts with how your facility operates: visitor traffic, access restrictions, working hours and surface requirements. Choose an industry to explore its priorities.
          </p>
          <p
            className="text-lg font-semibold"
            style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}
            data-testid="commercials-tagline"
          >
            One Facility. One Service Partner. One Integrated Solution.
          </p>
          {/* Iter-152 · Removed BUILD MY SERVICE PROGRAM + REQUEST A
              FACILITY ASSESSMENT CTAs per user request. */}
        </div>
      </section>
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} data-testid="commercials-industries-section" data-views="commercials">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            12 COMMERCIAL <span style={{ color: '#C9A962' }}>INDUSTRIES</span>
          </h2>
          {/* Iter-152 · 4-per-row grid; Mall lands in position 9 so
              Bank/Stadium/Airport fill columns 2/3/4 of row 3. */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(INDUSTRIES)
              .sort(([ka], [kb]) => {
                const ORDER = ['office','retail','gas_station','school','medical','warehouse','restaurant','church','mall','bank','stadium','airport'];
                const ai = ORDER.indexOf(ka);
                const bi = ORDER.indexOf(kb);
                return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
              })
              .map(([k, v]) => (
              <Card key={k} className="p-5 transition-all hover:scale-105 flex flex-col" style={{ backgroundColor: '#2D2D2D', border: '2px solid #4a4a4a' }} data-testid={`comm-industry-card-${k}`}>
                <span className="text-3xl mb-2">{v.icon}</span>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>{v.necs}</h3>
                <p className="text-xs mb-3" style={{ color: '#a0a0a0' }}>{v.label}</p>
                <Link to={`/commercial-services/${k}`} className="mt-auto text-xs font-bold uppercase tracking-widest" style={{ color: '#C9A962' }} data-testid={`comm-industry-link-${k}`}>
                  Explore {v.necs} →
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* Iter-152 · SERVICE PACKAGES block replaced with the running
          services carousel per user request. Package tiers remain
          selectable inside the commercial quote engine below. */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} data-testid="commercials-services-carousel-section" data-views="commercials">
        <div className="max-w-7xl mx-auto">
          <ServicesRunningCarousel testId="commercials-services-carousel" slides={COMMERCIAL_SERVICE_SLIDES} />
        </div>
      </section>
      <section id="commercials-quote" className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-testid="commercials-quote-section" data-views="commercials">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            GET A <span style={{ color: '#C9A962' }}>QUOTE</span>
          </h2>
          <p className="text-center text-lg max-w-3xl mx-auto mb-10" style={{ color: '#a0a0a0' }}>
            Tell us about your facility — your preliminary estimate is shared after you submit the form.
          </p>
          {showCommQuote ? (
            <CommercialQuoteEngine />
          ) : (
            <div className="text-center">
              <Button
                className="font-black uppercase tracking-widest px-10 py-6 text-base"
                style={{ backgroundColor: '#C9A962', color: '#2D2D2D', fontFamily: 'Barlow Condensed, sans-serif' }}
                onClick={() => setShowCommQuote(true)}
                data-testid="reveal-comm-quote-btn"
              >
                GET A QUOTE
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} id="packages" data-testid="service-packages-section" data-views="multifamily service-packages">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 tracking-tight text-white" style={{fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase'}}>
            SERVICE <span style={{ color: '#C9A962' }}>PACKAGES</span>
          </h2>
          <p className="text-center text-base sm:text-lg max-w-3xl mx-auto mb-6 italic" style={{ color: '#C9A962' }} data-testid="service-packages-custom-note">
            Custom-tailored service programs are available based on community operational needs.
          </p>
          <p className="text-center text-lg max-w-3xl mx-auto mb-8" style={{ color: '#a0a0a0' }}>
            Choose the perfect package for your property. All packages include our advanced QR code tracking system and 24/7 customer support.
            <span style={{ color: '#C9A962' }}> Select a package below to see your preliminary estimate in the Get a Quote form.</span>
          </p>

          {/* S13 — Multifamily packages only; commercial tiers live exclusively on the Commercials page. */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {displayedPackages.map((pkg, index) => (
              <Card
                key={index}
                className={`p-6 relative transition-all hover:scale-105 flex flex-col h-full ${pkg.recommended ? 'ring-2' : ''}`}
                style={{
                  backgroundColor: '#2D2D2D',
                  border: '2px solid #4a4a4a',
                  ...(pkg.recommended && { ringColor: '#C9A962', border: '2px solid #C9A962' })
                }}
                data-testid={`package-${pkg.name.toLowerCase().replace(/ /g, '-')}`}
              >
                {pkg.recommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold rounded-full" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}>
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-white leading-tight" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                    {pkg.name.toUpperCase()}
                  </h3>
                  {/* S6 — no pricing shown pre-quote; tier names + features only. */}
                  <div className="h-[2px] w-12 mx-auto mt-3" style={{ backgroundColor: '#C9A962' }} />
                </div>
                <ul className="space-y-3 mb-6 flex-1">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start text-sm text-white">
                      <CheckCircle2 className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#C9A962' }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full font-bold mt-auto"
                  style={{
                    backgroundColor: pkg.recommended ? '#C9A962' : 'transparent',
                    color: pkg.recommended ? '#2D2D2D' : '#C9A962',
                    border: pkg.recommended ? 'none' : '2px solid #C9A962'
                  }}
                  onClick={() => {
                    setQuotePreselect(pkg.name);
                    setShowMfQuote(true);
                    setTimeout(() => document.getElementById('get-a-quote')?.scrollIntoView({ behavior: 'smooth' }), 150);
                  }}
                >
                  GET A QUOTE
                </Button>
              </Card>
            ))}
          </div>

          {!showAllPackages && (
            <div className="text-center mt-8">
              <Button
                onClick={() => setShowAllPackages(true)}
                className="font-bold hover:opacity-90 transition"
                style={{ backgroundColor: '#000000', color: '#C9A962', borderColor: '#000000' }}
                data-testid="view-all-packages-btn"
              >
                VIEW ALL PACKAGES
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* E10 — public Get a Quote form (multifamily), live-wired to the
          admin Service Packages section. */}
      <section id="get-a-quote" className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-testid="get-a-quote-section" data-views="multifamily service-packages">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            GET A <span style={{ color: '#C9A962' }}>QUOTE</span>
          </h2>
          <p className="text-center text-lg max-w-3xl mx-auto mb-10" style={{ color: '#a0a0a0' }}>
            Select your package and contract term — your preliminary estimate is shared after you submit the form.
          </p>
          {showMfQuote ? (
            <MultifamilyQuoteForm preselectPackage={quotePreselect} />
          ) : (
            <div className="text-center">
              <Button
                className="font-black uppercase tracking-widest px-10 py-6 text-base"
                style={{ backgroundColor: '#C9A962', color: '#2D2D2D', fontFamily: 'Barlow Condensed, sans-serif' }}
                onClick={() => setShowMfQuote(true)}
                data-testid="reveal-mf-quote-btn"
              >
                GET A QUOTE
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* E8 — Multifamily specialty services + E9 contract rule */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} data-testid="mf-specialty-section" data-views="multifamily service-packages">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            ENHANCE YOUR <span style={{ color: '#C9A962' }}>COMMUNITY</span>
          </h2>
          <p className="text-center text-base max-w-3xl mx-auto mb-10" style={{ color: '#a0a0a0' }}>
            These services can be quoted separately or incorporated into an approved property program.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {['Pressure Washing', 'Window Cleaning', 'Floor Care', 'Floor Polishing', 'Floor Restoration', 'Bulk Removal', 'Property Appearance Patrol', 'Dumpster Treatment', 'Deep Cleaning', 'Amenity Cleaning'].map((s) => (
              <span key={s} className="px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#C9A962', border: '2px solid #C9A962', backgroundColor: '#2D2D2D' }} data-testid={`mf-specialty-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                {s}
              </span>
            ))}
          </div>
          <div className="max-w-3xl mx-auto rounded-xl p-6 text-center" style={{ backgroundColor: '#2D2D2D', border: '1px solid #4a4a4a' }} data-testid="mf-contract-rule">
            <p className="text-sm italic" style={{ color: '#a0a0a0', lineHeight: 1.7 }}>
              Recurring specialty and facility-care programs are generally structured around a minimum 12-month service agreement to support consistent staffing, equipment planning and service quality. Final terms are established in the executed service agreement.
            </p>
          </div>
        </div>
      </section>

      {/* Access Your App — direct entry points for Residents and Staff */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1f1f1f' }} data-testid="access-your-app-section" data-views="technology-apps">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white" style={{fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase'}}>
              ACCESS YOUR <span style={{ color: '#C9A962' }}>APP</span>
            </h2>
            <p className="text-lg mt-3" style={{ color: '#a0a0a0' }}>
              One login, two mobile experiences. Pick yours.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Resident App */}
            <div
              className="group rounded-2xl p-8 border-2 cursor-pointer transition-all hover:scale-[1.02] hover:border-[#C9A962]"
              style={{ backgroundColor: '#2D2D2D', borderColor: '#3D3D3D' }}
              onClick={() => navigate('/login')}
              data-testid="resident-app-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0 border-2 shadow-lg" style={{ borderColor: '#C9A962', backgroundColor: '#000' }}>
                  <img
                    src="/resident-icon-512.png"
                    alt="NECS Resident App icon"
                    className="w-full h-full object-cover"
                    data-testid="resident-app-icon-image"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.25em] mb-1" style={{ color: '#C9A962' }}>For Residents</p>
                  <h3 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                    NECS RESIDENT APP
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#a0a0a0' }}>
                    Live pickup ETA, "trash is outside" check-ins, bulk pickup requests, instant alerts in 7 languages.
                  </p>
                  <ul className="text-xs space-y-1 mb-5" style={{ color: '#a0a0a0' }}>
                    <li>• See the truck arrive — Uber-style live tracking</li>
                    <li>• One-tap alerts when service starts in your building</li>
                    <li>• Submit bulk pickup or service complaints with photos</li>
                  </ul>
                  <Button
                    className="font-bold w-full sm:w-auto"
                    style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                    onClick={(e) => { e.stopPropagation(); navigate('/login'); }}
                    data-testid="resident-app-login-btn"
                  >
                    Open Resident App →
                  </Button>
                </div>
              </div>
            </div>

            {/* Staff App */}
            <div
              className="group rounded-2xl p-8 border-2 cursor-pointer transition-all hover:scale-[1.02] hover:border-[#C9A962]"
              style={{ backgroundColor: '#2D2D2D', borderColor: '#3D3D3D' }}
              onClick={() => navigate('/login')}
              data-testid="staff-app-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0 border-2 shadow-lg" style={{ borderColor: '#C9A962', backgroundColor: '#000' }}>
                  <img
                    src="/staff-icon-512.png"
                    alt="NECS Staff App icon"
                    className="w-full h-full object-cover"
                    data-testid="staff-app-icon-image"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.25em] mb-1" style={{ color: '#C9A962' }}>For Drivers & Crew</p>
                  <h3 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                    NECS STAFF APP
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#a0a0a0' }}>
                    QR clock-in, GPS route tracking, vehicle safety checklist, training hub, daily reports.
                  </p>
                  <ul className="text-xs space-y-1 mb-5" style={{ color: '#a0a0a0' }}>
                    <li>• Start your route — live GPS visible to admin & PMs</li>
                    <li>• Mark "Arrived / Complete" per stop with one tap</li>
                    <li>• Pre & post-shift vehicle safety + fuel receipts</li>
                  </ul>
                  <Button
                    className="font-bold w-full sm:w-auto"
                    style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                    onClick={(e) => { e.stopPropagation(); navigate('/login'); }}
                    data-testid="staff-app-login-btn"
                  >
                    Open Staff App →
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-xs mt-6" style={{ color: '#71717a' }}>
            Same secure login for both — your role determines which app opens.
          </p>
          <div className="mt-10 flex flex-col items-center gap-3" data-testid="landing-app-store-section">
            <p className="text-xs uppercase tracking-[0.3em]" style={{ color: '#C9A962' }}>Get the mobile app</p>
            <img
              src="https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/n3d7bzfd_2094.png"
              alt="NECS Resident & Staff apps — Get it on Google Play and Download on the App Store"
              className="w-full max-w-xs sm:max-w-sm h-auto block rounded-xl"
              data-testid="landing-app-store-image"
            />
            <p className="text-[11px]" style={{ color: '#71717a' }}>
              Available on mobile via our web app. From your phone browser, use "Add to Home Screen" for an app-like experience.
            </p>
          </div>
        </div>
      </section>


      {/* App In Action Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} data-testid="app-in-action-section" data-views="technology-apps">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1 flex lg:justify-start justify-center">
            <div className="relative w-full max-w-sm sm:max-w-md rounded-xl overflow-hidden border shadow-2xl" style={{ borderColor: '#C9A962' }} data-testid="app-in-action-image-wrapper">
              <div className="relative w-full aspect-square">
                {appInActionSlides.map((slide, index) => (
                  <img
                    key={slide.image}
                    src={slide.image}
                    alt={slide.alt}
                    className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${slide.fit === 'contain' ? 'object-contain bg-black' : 'object-cover'} ${index === activeAppAction ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    data-testid={`app-in-action-slide-${index}`}
                  />
                ))}
              </div>
              <button
                type="button"
                aria-label="Previous"
                onClick={() => setActiveAppAction((p) => (p - 1 + appInActionSlides.length) % appInActionSlides.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border border-white/40 bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition"
                data-testid="app-in-action-prev"
              >‹</button>
              <button
                type="button"
                aria-label="Next"
                onClick={() => setActiveAppAction((p) => (p + 1) % appInActionSlides.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border border-white/40 bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition"
                data-testid="app-in-action-next"
              >›</button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="app-in-action-dots">
                {appInActionSlides.map((_, index) => (
                  <button
                    key={`aia-dot-${index}`}
                    type="button"
                    aria-label={`Slide ${index + 1}`}
                    onClick={() => setActiveAppAction(index)}
                    className={`h-2.5 w-2.5 rounded-full transition ${index === activeAppAction ? 'bg-[#C9A962]' : 'bg-white/40'}`}
                    data-testid={`app-in-action-dot-${index}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 tracking-tight text-white" style={{fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase'}}>
              APP <span style={{ color: '#C9A962' }}>IN ACTION</span>
            </h2>
            <p className="text-lg mb-6" style={{ color: '#a0a0a0' }}>
              The Noble Empire mobile app in real-time — keeping residents and property managers informed.
            </p>
            <div className="space-y-3 text-sm" style={{ color: '#d6d6d6' }}>
              <div className="flex items-start gap-3" data-testid="app-in-action-feature-1">
                <span className="w-2.5 h-2.5 rounded-full mt-2" style={{ backgroundColor: '#C9A962' }} />
                Live pickup confirmations
              </div>
              <div className="flex items-start gap-3" data-testid="app-in-action-feature-2">
                <span className="w-2.5 h-2.5 rounded-full mt-2" style={{ backgroundColor: '#C9A962' }} />
                Violation photo submissions
              </div>
              <div className="flex items-start gap-3" data-testid="app-in-action-feature-3">
                <span className="w-2.5 h-2.5 rounded-full mt-2" style={{ backgroundColor: '#C9A962' }} />
                Transparent property updates
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features Gallery — everything-in-one-app */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1F1F1F' }} data-testid="platform-features-section" data-views="technology-apps">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 text-xs font-bold tracking-widest rounded-full mb-3" style={{ backgroundColor: '#C9A962', color: '#1F1F1F' }}>
              EVERYTHING IN ONE APP
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
              A New Standard for <span style={{ color: '#C9A962' }}>Valet Trash Transparency</span>
            </h2>
            <p className="text-center text-lg max-w-3xl mx-auto mt-5" style={{ color: '#a0a0a0' }}>
              We don't just pick up trash — we run the entire resident, property and crew workflow on one platform. Every pickup is logged, every complaint is cross-referenced, and every dollar is accounted for.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              icon={<Eye className="h-7 w-7" />}
              title="Uber Trash — Live Transparency"
              body="Real-time feed showing tonight's job-close, residents who placed trash on time, who didn't, and any open complaints — answer 'did the resident actually put it out?' in 2 clicks."
              testid="feat-uber-trash"
            />
            <FeatureCard
              icon={<Languages className="h-7 w-7" />}
              title="7 Languages, Auto-Translated"
              body="Resident & crew apps speak English, Español, Tiếng Việt, Français, 中文, العربية and Português. Even SMS and email alerts auto-translate to each resident's preferred language."
              testid="feat-multi-lang"
            />
            <FeatureCard
              icon={<CheckCircle2 className="h-7 w-7" />}
              title="Resident Check-In"
              body="Residents tap 'Confirm: Trash Is Outside' from their app. Crew focuses on confirmed pickups first. On-time vs late tracked automatically."
              testid="feat-checkin"
            />
            <FeatureCard
              icon={<Boxes className="h-7 w-7" />}
              title="Bulk Pickup"
              body="Old couches, mattresses, large boxes — residents request a bulk pickup with photos. Per-property policy, scheduled by management, fees auto-billed."
              testid="feat-bulk"
            />
            <FeatureCard
              icon={<Camera className="h-7 w-7" />}
              title="AI Violation Reporting"
              body="Photos and AI-assisted classification help staff and residents identify possible violations. Repeat-offense alerts follow the property's policy."
              testid="feat-ai-violations"
            />
            <FeatureCard
              icon={<Scan className="h-7 w-7" />}
              title="QR Codes Everywhere"
              body="QR-code clock-in for staff, QR per unit for accurate pickup verification, and QR-verifiable certificates for completed training. Zero paper, full audit trail."
              testid="feat-qr"
            />
            <FeatureCard
              icon={<BarChart3 className="h-7 w-7" />}
              title="Automated PDF Reports"
              body="Daily QR pickup reports and monthly Trust Snapshots are generated and emailed automatically. Property managers always have proof of service ready for owners."
              testid="feat-reports"
            />
            <FeatureCard
              icon={<GraduationCap className="h-7 w-7" />}
              title="Training Hub + Certificates"
              body="Role-gated training modules for staff, PMs and residents — with publicly verifiable certificates of completion. New employees ramp in days, not weeks."
              testid="feat-training"
            />
            <FeatureCard
              icon={<Receipt className="h-7 w-7" />}
              title="Billing, Invoicing & A/R"
              body="Monthly invoices auto-generated, ACH/Wire/Check supported, automated overdue reminders, and a Revenue & A/R dashboard with DSO and collection rates per property."
              testid="feat-billing"
            />
          </div>

          <div className="mt-12 text-center">
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Every property gets a <strong className="text-white">Public Property Trust Page</strong> with verifiable monthly metrics — share it with owners or prospective tenants to prove your service quality.
            </p>
          </div>
        </div>
      </section>

      {/* Technology-Driven Operational Support — intro section above Admin Access on Technology page */}
      <section
        className="relative pt-24 sm:pt-28 py-16 px-4 sm:px-6 lg:px-8"
        style={{
          backgroundColor: '#2D2D2D',
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/ubxjik40_2258.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
        }}
        data-testid="tech-driven-operational-support-section"
        data-views="technology-apps"
      >
        {/* dark scrim so the white text stays readable over the phone-on-desk photo */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.78) 100%)' }}
          data-testid="tech-driven-bg-scrim"
        />
        <div className="relative max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] mb-3 text-center" style={{ color: '#C9A962' }}>Technology &amp; Mobile Apps</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            Technology-Driven <span style={{ color: '#C9A962' }}>Operational Support</span>
          </h2>
          <p className="text-lg mb-4 text-center" style={{ color: '#f5f5f5', lineHeight: 1.75 }}>
            NECS utilizes modern technology systems to improve communication, accountability, and operational transparency.
          </p>
          <p className="text-lg mb-3 text-center" style={{ color: '#f5f5f5', lineHeight: 1.75 }}>Our systems include:</p>
          <ul className="space-y-2 text-base mb-5 max-w-xl mx-auto" style={{ color: '#f5f5f5' }} data-testid="tech-driven-systems-list">
            <li>• QR-coded tracking systems</li>
            <li>• Real-time operational reporting</li>
            <li>• Resident notifications &amp; reminders</li>
            <li>• Digital service verification</li>
            <li>• Management visibility support</li>
            <li>• Operational communication tools</li>
          </ul>
          <p className="text-lg text-center" style={{ color: '#f5f5f5', lineHeight: 1.75 }}>
            This technology-driven approach helps provide greater transparency and service consistency for both management teams and residents.
          </p>
        </div>
      </section>

      {/* Admin Access Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#3D3D3D' }} data-testid="admin-access-portals-section" data-views="technology-apps">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 tracking-tight text-white" style={{fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase'}}>
            ADMIN <span style={{ color: '#C9A962' }}>ACCESS</span> PORTALS
          </h2>
          <p className="text-center text-lg max-w-3xl mx-auto mb-12" style={{ color: '#a0a0a0' }}>
            Different access levels for Company Management and Property Management with specialized dashboards and services
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-stretch">
            {/* Company Management */}
            <Card className="p-8 transition-all hover:scale-105 h-full flex flex-col" style={{ backgroundColor: '#2D2D2D', border: '2px solid #C9A962' }}>
              <div className="text-center flex flex-col h-full">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#C9A962' }}>
                  <Shield className="h-8 w-8" style={{ color: '#2D2D2D' }} />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                  COMPANY MANAGEMENT
                </h3>
                <p className="mb-6 min-h-[3rem] flex items-center justify-center" style={{ color: '#a0a0a0' }}>
                  Full admin access for Noble Empire internal management
                </p>
                <ul className="text-left space-y-2 mb-6 flex-1">
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Employee Management &amp; Scheduling
                  </li>
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Violation Review &amp; Approval
                  </li>
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Weekly Reports &amp; Performance
                  </li>
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Shift Swap Approvals
                  </li>
                </ul>
                <Button
                  className="w-full font-bold mt-auto"
                  style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                  onClick={() => navigate('/login')}
                >
                  COMPANY LOGIN
                </Button>
              </div>
            </Card>

            {/* Property Management */}
            <Card className="p-8 transition-all hover:scale-105 h-full flex flex-col" style={{ backgroundColor: '#2D2D2D', border: '2px solid #C9A962' }}>
              <div className="text-center flex flex-col h-full">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#C9A962' }}>
                  <Building className="h-8 w-8" style={{ color: '#2D2D2D' }} />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                  PROPERTY MANAGEMENT
                </h3>
                <p className="mb-6 min-h-[3rem] flex items-center justify-center" style={{ color: '#a0a0a0' }}>
                  Client access for Property Managers
                </p>
                <ul className="text-left space-y-2 mb-6 flex-1">
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Property Onboarding &amp; Setup
                  </li>
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Package Selection &amp; Billing
                  </li>
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Real-time GPS Tracking
                  </li>
                  <li className="flex items-center text-white">
                    <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#C9A962' }} />
                    Violation Reports &amp; History
                  </li>
                </ul>
                <Button
                  className="w-full font-bold mt-auto"
                  style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                  onClick={() => navigate('/live-demo')}
                  data-testid="property-manager-demo-pricing-button"
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  TRY LIVE DEMO — 30 SECONDS
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>


      {/* Residents Section — content shown only on Residents menu */}
      <section id="residents" className="pt-24 sm:pt-28 py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#2D2D2D' }} data-testid="residents-section" data-views="residents">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] mb-3 text-center" style={{ color: '#C9A962' }}>Welcome Residents</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 tracking-tight text-white text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            A Cleaner, Smarter &amp; More <span style={{ color: '#C9A962' }}>Comfortable Community Experience</span>
          </h2>

          <div className="space-y-5 text-lg" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
            <p>
              At Noble Empire Cleaning Services (NECS), we understand that residents deserve a cleaner, healthier, and more enjoyable living environment.
            </p>
            <p>
              Our mission is to help create communities where comfort, cleanliness, convenience, and professionalism work together to improve everyday living.
            </p>
            <p>
              Through our technology-driven valet trash, recycling support, eco-friendly cleaning, and community maintenance solutions, NECS proudly supports both residents and property management teams with dependable and resident-focused services.
            </p>
          </div>

          <h3 className="text-2xl sm:text-3xl font-bold mt-14 mb-5 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            Designed With <span style={{ color: '#C9A962' }}>Residents in Mind</span>
          </h3>

          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Left: copy + NECS provides list */}
            <div>
              <p className="text-lg mb-4" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
                Our services are structured to make daily living more convenient while helping maintain a cleaner and more attractive community environment.
              </p>
              <p className="text-lg mb-3" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>NECS proudly provides:</p>
              <ul className="space-y-2 text-base" style={{ color: '#d6d6d6' }} data-testid="residents-services-list">
                <li>• Reliable valet trash &amp; recycling collection</li>
                <li>• Cleaner hallways and shared spaces</li>
                <li>• Eco-friendly community support services</li>
                <li>• Quiet and professional service operations</li>
                <li>• Real-time resident notifications and updates</li>
                <li>• QR-coded service tracking technology</li>
                <li>• Cleaner dumpster and trash room areas</li>
                <li>• Community cleanliness enhancement support</li>
              </ul>
            </div>

            {/* Right: carousel */}
            <div className="relative" data-testid="residents-carousel">
              <div
                className="relative w-full overflow-hidden rounded-xl border"
                style={{ borderColor: '#C9A962', aspectRatio: '4 / 5', backgroundColor: '#1f1f1f' }}
              >
                {residentsCarousel.map((slide, index) => (
                  <img
                    key={index}
                    src={slide.image}
                    alt={slide.alt}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${index === residentsSlide ? 'opacity-100' : 'opacity-0'}`}
                    style={slide.objectPosition ? { objectPosition: slide.objectPosition } : undefined}
                    data-testid={`residents-carousel-image-${index}`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => setResidentsSlide((prev) => (prev - 1 + residentsCarousel.length) % residentsCarousel.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-white/40 bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition"
                aria-label="Previous resident image"
                data-testid="residents-carousel-prev"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={() => setResidentsSlide((prev) => (prev + 1) % residentsCarousel.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-white/40 bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition"
                aria-label="Next resident image"
                data-testid="residents-carousel-next"
              >
                ›
              </button>

              <div className="flex justify-center gap-2 mt-3" data-testid="residents-carousel-dots">
                {residentsCarousel.map((_, index) => (
                  <button
                    key={`residents-dot-${index}`}
                    type="button"
                    onClick={() => setResidentsSlide(index)}
                    className={`h-2.5 w-2.5 rounded-full transition ${index === residentsSlide ? 'bg-[#C9A962]' : 'bg-white/40'}`}
                    aria-label={`Go to resident image ${index + 1}`}
                    data-testid={`residents-carousel-dot-${index}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <h3 className="text-2xl sm:text-3xl font-bold mt-14 mb-5 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            Resident-Focused <span style={{ color: '#C9A962' }}>Technology</span>
          </h3>
          <p className="text-lg mb-3" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
            Our NECS Resident App helps residents stay informed and connected through:
          </p>
          <ul className="space-y-2 text-base mb-5" style={{ color: '#d6d6d6' }} data-testid="residents-app-features-list">
            <li>• Service notifications</li>
            <li>• Collection reminders</li>
            <li>• Community updates</li>
            <li>• Violation alerts</li>
            <li>• Service support requests</li>
            <li>• Real-time operational communication</li>
          </ul>
          <p className="text-lg" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
            We believe transparency and communication are essential to providing a better resident experience.
          </p>

          <h3 className="text-2xl sm:text-3xl font-bold mt-14 mb-5 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            Supporting <span style={{ color: '#C9A962' }}>Cleaner Communities</span>
          </h3>
          <div className="space-y-5 text-lg" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
            <p>
              At NECS, we are committed to maintaining professional service standards while helping properties reduce waste-related concerns, improve cleanliness, and create a more welcoming environment for everyone.
            </p>
            <p>
              Our eco-friendly approach and operational attention to detail help support healthier and more sustainable communities.
            </p>
          </div>

          <h3 className="text-2xl sm:text-3xl font-bold mt-14 mb-5 tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
            Our <span style={{ color: '#C9A962' }}>Commitment</span>
          </h3>
          <div className="space-y-5 text-lg" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
            <p>
              We are proud to serve your community with professionalism, consistency, respect, and care.
            </p>
            <p>
              Thank you for allowing Noble Empire Cleaning Services to support your home and community experience.
            </p>
          </div>

          <div className="mt-12 pt-8 text-center" style={{ borderTop: '1px solid #4a4a4a' }}>
            <p className="text-base font-semibold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}>
              Noble Empire Cleaning Services
            </p>
            <p className="text-sm mt-1" style={{ color: '#C9A962' }}>
              Professional Property Support &amp; Sustainability Solutions
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          MANAGEMENT & PROPERTY PARTNERS — Iter-155 · Full rebuild.
          Sections: Hero · One Property Multiple Needs · Built for
          Property Management · Multifamily Community Support ·
          Commercial Facility Support (12 verticals) · Technology-Driven
          Accountability · Long-Term Service Partnerships · Professional
          Standards · The NECS Difference · NECS Integrated Facility Care.
      ============================================================ */}

      {/* Management Section 1 — Hero */}
      <div className="management-page-background" style={{ '--management-background-image': 'url("/images/management-strategy-meeting.webp")' }} data-views="management" data-testid="management-page-background">
      <section
        id="management"
        className="relative pt-28 sm:pt-32 pb-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-hero"
        data-views="management"
      >
        <div className="relative max-w-5xl mx-auto text-center">
          <p className="text-xs sm:text-sm uppercase mb-4" style={{ color: '#C9A962', letterSpacing: '0.35em' }} data-testid="management-hero-eyebrow">
            Integrated Facility Care
          </p>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em' }}
            data-testid="management-hero-title"
          >
            Management and <span style={{ color: '#C9A962' }}>Property Partners</span>
          </h1>
          <p
            className="text-base sm:text-lg max-w-3xl mx-auto mb-8"
            style={{ color: '#e5e5e5', lineHeight: 1.6 }}
            data-testid="management-hero-subtitle"
          >
            Professional Operational Support for <span style={{ color: '#C9A962' }}>Modern Properties</span>
          </p>
          <div className="text-left max-w-3xl mx-auto space-y-4 text-base sm:text-lg" style={{ color: '#e5e5e5', lineHeight: 1.75 }} data-testid="management-hero-body">
            <p>
              Keep service decisions, site access and follow-up in one place.
            </p>
            <p>
              Management teams can review service records, raise exceptions and coordinate priorities with their NECS contact.
            </p>
            <p><Link to="/?view=services" className="text-[#C9A962] underline" data-testid="management-service-scope-link">Explore service scope →</Link></p>
          </div>
        </div>
      </section>

      {/* Management Section 2 — One Property. Multiple Needs. One Service Partner. */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-one-partner"
        data-views="management"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Coordinated Program</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              One Property. Multiple Needs. <span style={{ color: '#C9A962' }}>One Service Partner.</span>
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              NECS combines multiple compatible services into one coordinated operational program.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: ClipboardCheck, title: 'Agree the Scope', body: 'Confirm service areas, access requirements and the responsibility for each task.' },
              { icon: Clock, title: 'Coordinate the Schedule', body: 'Align visits with operating hours, resident activity and site restrictions.' },
              { icon: BarChart3, title: 'Review and Follow Up', body: 'Use service records and documented exceptions to plan the next steps.' },
            ].map((c) => (
              <div
                key={c.title}
                className="rounded-2xl p-6 border transition hover:-translate-y-1"
                style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.35)' }}
                data-testid={`management-service-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="flex items-center justify-center w-11 h-11 rounded-full mb-4" style={{ backgroundColor: 'rgba(201,169,98,0.15)' }}>
                  <c.icon className="w-5 h-5" style={{ color: '#C9A962' }} />
                </div>
                <p className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {c.title}
                </p>
                <p className="text-sm" style={{ color: '#a0a0a0', lineHeight: 1.65 }}>{c.body}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-base sm:text-lg mt-10 max-w-3xl mx-auto italic" style={{ color: '#e5e5e5', lineHeight: 1.7 }} data-testid="management-one-partner-closing">
            Instead of managing several disconnected vendors, clients can coordinate compatible services through one accountable partner.
          </p>
        </div>
      </section>

      {/* Management Section 3 — Built for Property Management */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-built-for-pm"
        data-views="management"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>For Property Management</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Built for <span style={{ color: '#C9A962' }}>Property Management</span>
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              NECS service programs are designed to help management teams:
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl mx-auto" data-testid="management-pm-benefits-list">
            {[
              'Improve resident and customer satisfaction',
              'Reduce service-related complaints',
              'Maintain cleaner common areas and service zones',
              'Enhance property appearance and curb appeal',
              'Support sustainability objectives',
              'Improve operational visibility',
              'Reduce vendor coordination',
              'Simplify service management',
              'Protect long-term property value',
              'Improve overall facility presentation',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-base" style={{ color: '#e5e5e5', lineHeight: 1.6 }}>
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#C9A962' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Management Section 4 — Multifamily Community Support */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-multifamily-support"
        data-views="management"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Multifamily</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Multifamily Community <span style={{ color: '#C9A962' }}>Support</span>
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              NECS provides integrated service programs designed specifically for apartment communities and managed residential properties.
            </p>
          </div>
          <p className="text-center text-sm uppercase mb-6" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>Community Planning Priorities</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-5xl mx-auto" data-testid="management-multifamily-services">
            {[
              'Resident access and collection windows',
              'Move-out and peak-volume planning',
              'Shared-amenity schedules',
              'Resident communication and follow-up',
            ].map((s) => (
              <div
                key={s}
                className="rounded-lg px-3 py-3 border text-center"
                style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.25)' }}
                data-testid={`management-multifamily-svc-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <p className="text-xs sm:text-sm font-semibold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {s}
                </p>
              </div>
            ))}
          </div>
          <p className="text-center text-base sm:text-lg mt-10 max-w-3xl mx-auto italic" style={{ color: '#e5e5e5', lineHeight: 1.7 }} data-testid="management-multifamily-closing">
            <Link to="/?view=multifamily" className="text-[#C9A962] underline" data-testid="management-multifamily-details">Explore the multifamily program →</Link>
          </p>
        </div>
      </section>

      {/* Management Section 5 — Commercial Facility Support */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-commercial-support"
        data-views="management"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Commercial</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Commercial Facility <span style={{ color: '#C9A962' }}>Support</span>
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              NECS also provides structured service programs for commercial properties and specialized facilities.
            </p>
          </div>
          <div className="text-center"><Link to="/?view=commercials" className="text-[#C9A962] underline" data-testid="management-commercial-details">Find your industry’s service program →</Link></div>

          <div className="mt-12 max-w-4xl mx-auto">
            <p className="text-center text-sm sm:text-base font-semibold mb-5" style={{ color: '#ffffff', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Each industry receives customized service programs based on:
            </p>
            <ul className="grid sm:grid-cols-3 gap-x-6 gap-y-2" data-testid="management-customization-factors">
              {['Facility size', 'Traffic volume', 'Operating hours', 'Service frequency', 'Waste volume', 'Floor types', 'Exterior conditions', 'Security requirements', 'Special operational needs'].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm" style={{ color: '#e5e5e5' }}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#C9A962' }} />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Management Section 6 — Technology-Driven Accountability */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-technology"
        data-views="management"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Technology</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Technology-Driven <span style={{ color: '#C9A962' }}>Accountability</span>
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              Professional service should be visible. NECS integrates technology to improve communication, documentation, and transparency.
            </p>
          </div>
          <p className="text-center text-sm uppercase mb-6" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>Our systems may include:</p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl mx-auto" data-testid="management-tech-list">
            {[
              'QR-based service verification',
              'GPS route confirmation',
              'Digital service records',
              'Resident communication tools',
              'Property management dashboards',
              'Operational reports',
              'Issue reporting',
              'Photo documentation',
              'Mobile applications',
              'Real-time notifications',
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-base" style={{ color: '#e5e5e5', lineHeight: 1.6 }}>
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#C9A962' }} />
                <span>{t}</span>
              </li>
            ))}
          </ul>

          <div
            className="mt-12 mx-auto max-w-3xl rounded-2xl p-6 sm:p-8 border text-center"
            style={{ backgroundColor: '#1f1f1f', borderColor: '#C9A962' }}
            data-testid="management-tech-callout"
          >
            <p className="text-lg sm:text-xl font-bold text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Technology does not <span style={{ color: '#C9A962' }}>replace service.</span>
            </p>
            <p className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Technology strengthens <span style={{ color: '#C9A962' }}>accountability.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Management Section 7 — Long-Term Service Partnerships */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-partnerships"
        data-views="management"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Partnerships</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Long-Term Service <span style={{ color: '#C9A962' }}>Partnerships</span>
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              NECS believes recurring property operations work best through structured partnerships. Commercial and multifamily programs are generally designed around:
            </p>
          </div>

          <p className="text-center text-sm uppercase mb-4" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>Initial Service Terms</p>
          <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto mb-12" data-testid="management-terms">
            {['12 Months', '24 Months', '36 Months'].map((term) => (
              <div
                key={term}
                className="rounded-xl py-6 px-3 border text-center transition hover:-translate-y-1"
                style={{ backgroundColor: '#2D2D2D', borderColor: 'rgba(201,169,98,0.4)' }}
                data-testid={`management-term-${term.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.03em' }}>
                  {term}
                </p>
              </div>
            ))}
          </div>

          <p className="text-center text-sm uppercase mb-6" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>Longer agreements provide:</p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl mx-auto" data-testid="management-longer-benefits">
            {[
              'Stable staffing',
              'Better operational planning',
              'Consistent service quality',
              'Equipment investment',
              'Improved pricing stability',
              'Greater service continuity',
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-base" style={{ color: '#e5e5e5', lineHeight: 1.6 }}>
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#C9A962' }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="text-center text-base sm:text-lg mt-10 max-w-3xl mx-auto italic" style={{ color: '#e5e5e5', lineHeight: 1.7 }} data-testid="management-partnerships-closing">
            Eligible clients may transition to flexible renewal options following the initial service period according to agreement terms.
          </p>
        </div>
      </section>

      {/* Management Section 8 — Professional Standards */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-professional-standards"
        data-views="management"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Standards</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Professional <span style={{ color: '#C9A962' }}>Standards</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Users, title: 'Professional People', body: 'Uniformed, trained, background-checked employees.' },
              { icon: ClipboardCheck, title: 'Accountability', body: 'Digital verification and documented service activity.' },
              { icon: Shield, title: 'Safety', body: 'OSHA-aligned procedures and controlled operations.' },
              { icon: Leaf, title: 'Responsible Practices', body: 'Environmental considerations where operationally appropriate.' },
              { icon: Headphones, title: 'Responsiveness', body: 'Structured communication and support.' },
              { icon: Rocket, title: 'Continuous Improvement', body: 'Ongoing evaluation of service quality and client needs.' },
            ].map((c) => (
              <div
                key={c.title}
                className="rounded-2xl p-6 border transition hover:-translate-y-1"
                style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.35)' }}
                data-testid={`management-standard-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="flex items-center justify-center w-11 h-11 rounded-full mb-4" style={{ backgroundColor: 'rgba(201,169,98,0.15)' }}>
                  <c.icon className="w-5 h-5" style={{ color: '#C9A962' }} />
                </div>
                <p className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {c.title}
                </p>
                <p className="text-sm" style={{ color: '#a0a0a0', lineHeight: 1.65 }}>{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Management Section 9 — The NECS Difference */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="management-necs-difference"
        data-views="management"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>The Difference</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              The <span style={{ color: '#C9A962' }}>NECS Difference</span>
            </h2>
            <div className="max-w-3xl mx-auto text-base sm:text-lg space-y-2" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              <p>Set expectations before work begins and review results against the agreed scope.</p>
            </div>
          </div>
          <p className="text-center text-sm uppercase mb-6" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>Your review checkpoints</p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 max-w-5xl mx-auto" data-testid="management-difference-list">
            {[
              'Agreed scope and frequency',
              'Documented service evidence',
              'Exceptions and next steps',
            ].map((d) => (
              <li key={d} className="flex items-start gap-3 text-base" style={{ color: '#e5e5e5', lineHeight: 1.6 }}>
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#C9A962' }} />
                <span>{d}</span>
              </li>
            ))}
          </ul>
          <p className="text-center text-base sm:text-lg mt-10 italic" style={{ color: '#ffffff', lineHeight: 1.7, fontWeight: 600 }} data-testid="management-difference-closing">
            <Link to="/quote" className="text-[#C9A962] underline" data-testid="management-build-program">Build your program →</Link>
          </p>
        </div>
      </section>

      {/* Management Section 10 — NECS Integrated Facility Care */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8 relative"
        data-testid="management-integrated-close"
        data-views="management"
      >
        <div className="max-w-4xl mx-auto text-center relative">
          <p className="text-xs uppercase mb-4" style={{ color: '#C9A962', letterSpacing: '0.35em' }}>Closing Statement</p>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.03em' }}
          >
            NECS Integrated <span style={{ color: '#C9A962' }}>Facility Care</span>
          </h2>
          <div
            className="inline-block px-6 py-3 rounded-full mb-8"
            style={{ backgroundColor: 'rgba(201,169,98,0.12)', border: '1px solid #C9A962' }}
            data-testid="management-integrated-tagline"
          >
            <p className="text-base sm:text-lg italic" style={{ color: '#C9A962', letterSpacing: '0.03em' }}>
              One Facility. One Service Partner. One Integrated Solution.
            </p>
          </div>
          <div className="grid gap-3 max-w-2xl mx-auto" data-testid="management-integrated-callout-lines">
            {[
              'Cleaner Spaces.',
              'Safer Communities.',
              'Better-Maintained Properties.',
              'Technology-Driven Accountability.',
              'Long-Term Partnership.',
            ].map((line) => (
              <p
                key={line}
                className="text-lg sm:text-xl font-bold text-white"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </section>
      </div>

      {/* ============================================================
          ABOUT NECS — Iter-154 · 10-section restructure
          Sections: 1 Hero · 2 Who We Are · 3 Built for the Way Properties
          Operate · 4 Industries We Serve · 5 One Partner Multiple Needs
          · 6 Technology-Enabled Accountability · 7 Responsible Operations
          · 8 Our Commitment to Service · 9 More Than a Cleaning Company
          · 10 Mission + CTA
      ============================================================ */}

      {/* Section 1 — Hero */}
      <div className="about-page-background" style={{ '--about-background-image': 'url("/images/about-necs-team.webp")' }} data-views="about" data-testid="about-page-background">
      <section
        id="about"
        className="relative pt-28 sm:pt-32 pb-20 px-4 sm:px-6 lg:px-8"
        data-testid="about-hero"
        data-views="about"
      >
        <div className="relative max-w-6xl mx-auto text-center">
          <p className="text-xs sm:text-sm uppercase mb-4" style={{ color: '#C9A962', letterSpacing: '0.35em' }} data-testid="about-hero-eyebrow">
            Integrated Facility Care
          </p>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em' }}
            data-testid="about-hero-title"
          >
            About <span style={{ color: '#C9A962' }}>NECS</span>
          </h1>
          <p
            className="text-base sm:text-lg max-w-3xl mx-auto"
            style={{ color: '#e5e5e5', lineHeight: 1.65 }}
            data-testid="about-hero-subtitle"
          >
            Professional Property Support and <span style={{ color: '#C9A962' }}>Sustainability Solutions</span>
          </p>
        </div>
      </section>

      {/* Section 2 — Who We Are */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="about-who-we-are"
        data-views="about"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Who We Are</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              A Trusted <span style={{ color: '#C9A962' }}>Property Support Partner</span>
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
              Noble Empire Cleaning Services, LLC is based in Flower Mound, Texas. Our team’s work is guided by professionalism, accountable service and responsible operational practices.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Award, label: 'Professionalism', body: 'Uniformed, badge-verified crews and disciplined operational standards on every property.' },
              { icon: ClipboardCheck, label: 'Accountability', body: 'Digital verification, photo-stamped reporting, and per-property fine policies you can audit.' },
              { icon: Leaf, label: 'Sustainability', body: 'Recycling support, eco-friendly chemistries, and waste-diversion programs at the core of every route.' },
              { icon: Handshake, label: 'Exceptional Customer Service', body: 'Direct manager access, resident-facing communication, and same-day response protocols.' },
            ].map((p) => (
              <div
                key={p.label}
                className="rounded-2xl p-6 text-left border transition hover:-translate-y-1"
                style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.35)' }}
                data-testid={`about-principle-${p.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="flex items-center justify-center w-11 h-11 rounded-full mb-4" style={{ backgroundColor: 'rgba(201,169,98,0.12)' }}>
                  <p.icon className="w-5 h-5" style={{ color: '#C9A962' }} />
                </div>
                <p className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {p.label}
                </p>
                <p className="text-sm" style={{ color: '#a0a0a0', lineHeight: 1.65 }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 — Built for the Way Properties Operate */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="about-integrated-model"
        data-views="about"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Integrated Service Model</p>
              <h2
                className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-5"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
              >
                Built for the Way <span style={{ color: '#C9A962' }}>Properties Operate</span>
              </h2>
              <p className="text-base mb-4" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
                Our operating model connects planning, field delivery and review. Responsibilities are defined before work starts, and exceptions have a clear path for follow-up.
              </p>
              <p className="text-base" style={{ color: '#d6d6d6', lineHeight: 1.75 }}>
                <Link to="/?view=management" className="text-[#C9A962] underline" data-testid="about-management-link">How we work with management teams →</Link>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: ClipboardCheck, label: 'Plan' },
                { icon: Users, label: 'Deliver' },
                { icon: ShieldCheck, label: 'Verify' },
                { icon: BarChart3, label: 'Improve' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl px-4 py-4 border flex items-center gap-3"
                  style={{ backgroundColor: '#2D2D2D', borderColor: 'rgba(201,169,98,0.25)' }}
                  data-testid={`about-servicelane-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <s.icon className="w-5 h-5 flex-shrink-0" style={{ color: '#C9A962' }} />
                  <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 — Industries We Serve */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="about-industries"
        data-views="about"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Where We Work</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Industries <span style={{ color: '#C9A962' }}>We Serve</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div
              className="rounded-2xl p-7 border relative overflow-hidden"
              style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.35)' }}
              data-testid="about-industry-multifamily"
            >
              <Building className="w-8 h-8 mb-4" style={{ color: '#C9A962' }} />
              <p className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Multifamily
              </p>
              <p className="text-sm mb-4" style={{ color: '#a0a0a0', lineHeight: 1.7 }}>
                Luxury and workforce apartment communities across DFW and beyond — valet trash, recycling, porter, dumpster treatment, breezeways, common areas, and resident-facing communication.
              </p>
              <p className="text-xs uppercase" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
                Class A · B · C Communities
              </p>
            </div>
            <div
              className="rounded-2xl p-7 border relative overflow-hidden"
              style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.35)' }}
              data-testid="about-industry-commercial"
            >
              <Building2 className="w-8 h-8 mb-4" style={{ color: '#C9A962' }} />
              <p className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Commercial · 12 Verticals
              </p>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mb-4" style={{ color: '#d6d6d6' }}>
                {['Office Pro','Retail Clean','Station Care','Campus Clean','MedSafe','Industrial','DineClean','MallCare','ChurchCare','BankCare','StadiumCare','AirportCare'].map((v) => (
                  <li key={v} className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full" style={{ backgroundColor: '#C9A962' }} />
                    {v}
                  </li>
                ))}
              </ul>
              <p className="text-xs uppercase" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
                Standard + Strategic Enterprise
              </p>
            </div>
            <div
              className="rounded-2xl p-7 border relative overflow-hidden"
              style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.35)' }}
              data-testid="about-industry-specialized"
            >
              <Trophy className="w-8 h-8 mb-4" style={{ color: '#C9A962' }} />
              <p className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Specialized Facilities
              </p>
              <p className="text-sm mb-4" style={{ color: '#a0a0a0', lineHeight: 1.7 }}>
                Banks, stadiums, and airports operate under regulated procedures. NECS services these accounts only within client-authorized security zones with facility-assessed programs and executive-approved pricing.
              </p>
              <p className="text-xs uppercase" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
                Vault-Aware · TSA-Aware · Event-Aware
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 — One Partner. Multiple Property Needs. */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="about-ecosystem"
        data-views="about"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Service Ecosystem</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-3"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              One Partner. <span style={{ color: '#C9A962' }}>Multiple Property Needs.</span>
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.65 }}>
              Explore service scope or choose a facility-specific program.
            </p>
          </div>
          <div className="flex flex-wrap gap-6 justify-center" data-testid="about-next-steps">
            <Link to="/?view=services" className="text-[#C9A962] underline" data-testid="about-services-link">Service scope →</Link>
            <Link to="/?view=commercials" className="text-[#C9A962] underline" data-testid="about-industries-link">Industry programs →</Link>
            <Link to="/quote" className="text-[#C9A962] underline" data-testid="about-quote-link">Build your program →</Link>
          </div>
        </div>
      </section>

      {/* Section 6 — Technology-Enabled Accountability */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="about-technology"
        data-views="about"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Service Visibility</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-3"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Technology-Enabled <span style={{ color: '#C9A962' }}>Accountability</span>
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: '#d6d6d6', lineHeight: 1.65 }}>
              Every visit digitally verified. Every report timestamped. Every dashboard live.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Smartphone, title: 'Mobile Apps', body: 'Resident, staff, and PM apps handle service requests, alerts, and communication in real time.' },
              { icon: Scan, title: 'QR Code Tracking', body: 'Every trash room, common area, and route stop is QR-verified — no missed touchpoints, no guesswork.' },
              { icon: BarChart3, title: 'Real-Time Reporting', body: 'Photo-stamped violation reports, monthly health digests, and live GPS truck tracking on demand.' },
              { icon: Eye, title: 'Client Dashboards', body: 'Property Manager and Owner dashboards with contract, invoice, service, and inspection visibility.' },
            ].map((t) => (
              <div
                key={t.title}
                className="rounded-2xl p-6 border transition hover:-translate-y-1"
                style={{ backgroundColor: '#2D2D2D', borderColor: 'rgba(201,169,98,0.3)' }}
                data-testid={`about-tech-${t.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="flex items-center justify-center w-11 h-11 rounded-full mb-4" style={{ backgroundColor: 'rgba(201,169,98,0.15)' }}>
                  <t.icon className="w-5 h-5" style={{ color: '#C9A962' }} />
                </div>
                <p className="text-base font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t.title}
                </p>
                <p className="text-sm" style={{ color: '#a0a0a0', lineHeight: 1.65 }}>{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 — Responsible Operations */}
      <section
        className="py-16 px-4 sm:px-6 lg:px-8"
        data-testid="about-responsible-ops"
        data-views="about"
      >
        <div className="max-w-5xl mx-auto">
          <div
            className="rounded-2xl border p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            style={{ backgroundColor: '#1f1f1f', borderColor: 'rgba(201,169,98,0.4)' }}
          >
            <div
              className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-full"
              style={{ backgroundColor: 'rgba(201,169,98,0.15)' }}
              aria-hidden="true"
            >
              <Shield className="w-7 h-7" style={{ color: '#C9A962' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase mb-2" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Responsible Operations</p>
              <h2
                className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
              >
                Safe Chemistry. Trained Crews. <span style={{ color: '#C9A962' }}>Compliant Operations.</span>
              </h2>
              <p className="text-sm sm:text-base mb-4" style={{ color: '#d6d6d6', lineHeight: 1.7 }}>
                Every NECS crew is trained on safe chemical handling, SDS-driven product selection, jobsite compliance, and vertical-specific protocols — from bloodborne pathogen procedures in medical facilities to TSA-compliant zoning at airports.
              </p>
              <button
                type="button"
                onClick={() => goToView('mission')}
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest transition hover:opacity-80"
                style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}
                data-testid="about-safety-link"
              >
                View the Full NECS Safety Program <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8 — Our Commitment to Service */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        data-testid="about-commitments"
        data-views="about"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase mb-3" style={{ color: '#C9A962', letterSpacing: '0.3em' }}>Our Commitment</p>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Our Commitment to <span style={{ color: '#C9A962' }}>Service</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: CheckCircle2, title: 'Consistent Delivery', body: 'Route discipline, backup crews, and Automated daily operational reporting.' },
              { icon: Eye, title: 'Transparent Reporting', body: 'Photo-verified service logs and monthly performance digests every stakeholder can access.' },
              { icon: Leaf, title: 'Sustainability First', body: 'Recycling support, eco-friendly chemistries, and measurable waste-diversion outcomes.' },
              { icon: ShieldCheck, title: 'Safety & Compliance', body: 'OSHA-aligned protocols, SDS-based product selection, and vertical-specific safety training.' },
              { icon: Users, title: 'Verified Crews', body: 'Uniformed, background-checked, badge-verified, and training-tracked professionals on every route.' },
              { icon: Headphones, title: 'Direct Manager Access', body: 'One accountable NECS manager per property — reachable directly, not through a call center.' },
            ].map((c) => (
              <div
                key={c.title}
                className="rounded-2xl p-6 border transition hover:-translate-y-1"
                style={{ backgroundColor: '#2D2D2D', borderColor: 'rgba(201,169,98,0.3)' }}
                data-testid={`about-commitment-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <c.icon className="w-6 h-6 mb-4" style={{ color: '#C9A962' }} />
                <p className="text-base font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {c.title}
                </p>
                <p className="text-sm" style={{ color: '#a0a0a0', lineHeight: 1.65 }}>{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 9 — More Than a Cleaning Company */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8 relative"
        data-testid="about-elevation"
        data-views="about"
      >
        <div className="max-w-4xl mx-auto text-center relative">
          <p className="text-xs uppercase mb-4" style={{ color: '#C9A962', letterSpacing: '0.35em' }}>Brand Positioning</p>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.03em' }}
          >
            More Than a <span style={{ color: '#C9A962' }}>Cleaning Company</span>
          </h2>
          <p className="text-base sm:text-lg mb-5" style={{ color: '#e5e5e5', lineHeight: 1.75 }}>
            NECS is not a mop, a broom, or a truck. NECS is a facility care partner — the single relationship that keeps a property clean, compliant, presentable, and predictable across every service line it depends on.
          </p>
          <p className="text-base sm:text-lg italic" style={{ color: '#C9A962', lineHeight: 1.75 }}>
            One Facility. One Service Partner. One Integrated Solution.
          </p>
        </div>
      </section>
      </div>

      {/* Section 10 — Mission + CTA removed per user request (Iter-156);
          the merged final-cta hero on every page already carries the
          Facility Assessment CTA and NECS mission tagline. */}

      {/* E20 — Management: commercial account expansion section removed
          in Iter-155 rebuild; content is now covered by the full
          Management & Property Partners restructure above. */}


      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1f1f1f' }} data-testid="faq-section" data-views="faq">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#C9A962' }}>Frequently Asked</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
              What Can We <span style={{ color: '#C9A962' }}>Help You With?</span>
            </h2>
          </div>
          {(() => {
            // Iter-149 · Section 41 — FAQ overhaul for the NECS Integrated
            // Facility Care platform. 8 categories · concise, customer-facing.
            // Internal Knowledge Base FAQs on the resident/PM/staff
            // dashboards are untouched.
            const FAQ_GROUPS = [
              {
                category: 'General',
                items: [
                  { q: 'What is NECS Integrated Facility Care?',
                    a: 'One professionally managed relationship for multiple recurring facility needs — waste management, janitorial, sanitation, floor care, exterior maintenance and specialty services — coordinated for one facility.' },
                  { q: 'What types of facilities does NECS service?',
                    a: 'Multifamily communities and 12 commercial verticals: Office, Retail, Gas Station, School, Medical, Industrial/Warehouse, Restaurant, Mall, Church, Bank, Stadium, and Airport.' },
                  { q: 'What does "integrated facility care" mean?',
                    a: 'NECS coordinates cleaning, waste, floor care, exterior appearance and specialty services into a single program built around your facility\'s operational requirements.' },
                ],
              },
              {
                category: 'Commercial',
                items: [
                  { q: 'What are the 12 commercial facility verticals?',
                    a: 'Office Pro, Retail Clean, Station Care, Campus Clean, MedSafe, Industrial, DineClean, MallCare, ChurchCare (Standard Commercial), plus BankCare, StadiumCare and AirportCare (Strategic Enterprise).' },
                  { q: 'What is the difference between Essential, Professional and Executive Care?',
                    a: 'Essential Care maintains the facility. Professional Care maintains and improves with specialty services and inspections. Executive Care delivers a full integrated program with dedicated management, digital verification and priority coordination.' },
                  { q: 'What is a Strategic Enterprise Account?',
                    a: 'Bank, Stadium and Airport programs. They require facility assessment, enhanced proposal review, operational-complexity analysis, and executive approval before final pricing.' },
                  { q: 'How does NECS handle security-sensitive facilities such as banks and airports?',
                    a: 'Secure and restricted-access areas are serviced only according to the client\'s authorized security procedures. NECS never implies unrestricted access to vaults, cash rooms, or restricted aviation zones.' },
                  { q: 'What is a Facility Assessment and why is it required for complex accounts?',
                    a: 'An on-site scoping visit that validates square footage, zones, traffic, complexity and specialty needs. It ensures the final proposal accurately reflects your facility rather than a square-foot estimate alone.' },
                ],
              },
              {
                category: 'Quote & Pricing',
                items: [
                  { q: 'How does the NECS quote engine work?',
                    a: 'Select your facility type, provide facility information, choose Essential/Professional/Executive Care, add specialty services and contract duration, then receive a preliminary estimate that is confirmed by facility assessment.' },
                  { q: 'Why does my preliminary estimate require a facility assessment?',
                    a: 'A facility assessment validates scope and complexity so your final proposal reflects the real service program — not just square footage.' },
                  { q: 'What factors affect facility-care pricing?',
                    a: 'Facility type, square footage, service frequency, traffic, operating hours, zones, floors, waste volume, specialty services and contract duration.' },
                  { q: 'Are Stadium and Airport accounts priced differently?',
                    a: 'Yes. Stadium and Airport programs use event/traffic-driven pricing inputs (attendance, events per month, gates, restrooms, turnaround windows) in addition to facility size.' },
                ],
              },
              {
                category: 'Contracts',
                items: [
                  { q: 'What is the minimum contract term?',
                    a: 'Every recurring facility-care program begins with a 12-month minimum commitment.' },
                  { q: 'What are the benefits of a 24 or 36-month agreement?',
                    a: 'Longer-term agreements provide greater operational planning stability and preferred contracted pricing.' },
                  { q: 'What happens after my contract expires?',
                    a: 'Eligible clients may renew for 12, 24 or 36 months, continue month-to-month at the applicable flexible-term rate, or terminate according to the executed agreement.' },
                  { q: 'What is the month-to-month rate?',
                    a: 'Month-to-month pricing is higher than fixed-term contracted pricing. The exact premium is set by NECS Finance and provided in your renewal materials.' },
                ],
              },
              {
                category: 'Payments',
                items: [
                  { q: 'What payment methods does NECS accept?',
                    a: 'ACH (primary for recurring B2B programs), credit/debit card, and approved alternatives such as check and wire transfer.' },
                  { q: 'What are NECS payment terms?',
                    a: 'NET 30. Invoices are issued on Day 0 and due on Day 30. A 5-calendar-day administrative courtesy period is provided; it does not change the contractual due date.' },
                  { q: 'What is ACH and why does NECS recommend it for recurring programs?',
                    a: 'ACH is an electronic bank-to-bank payment method with lower processing costs and reliable timing — ideal for recurring facility-care programs.' },
                  { q: 'What happens if a payment is late?',
                    a: 'NECS issues reminders on a defined schedule and escalates through finance, account management and executive review as needed, following your executed agreement.' },
                ],
              },
              {
                category: 'Multifamily',
                items: [
                  { q: 'What multifamily services does NECS provide?',
                    a: 'Valet Trash, recycling, bulk removal, dumpster treatment, trash-room maintenance, Property Appearance Patrol, pressure washing, window cleaning, floor care, amenity cleaning and day porter services.' },
                  { q: 'How does Valet Trash service work?',
                    a: 'Residents place approved containers outside their doors within the service window; NECS collects nightly per the property contract. No re-runs.' },
                  { q: 'How do residents submit service requests?',
                    a: 'Through the resident portal — missed pickup, gate-code help, violations and general questions all route through the resident app.' },
                ],
              },
              {
                category: 'Technology',
                items: [
                  { q: 'Does NECS have a mobile app?',
                    a: 'Available on mobile via our web app. Residents, property managers and staff can access NECS on any modern smartphone browser.' },
                  { q: 'How do I access my customer portal?',
                    a: 'Sign in from the NECS website. Residents use the resident portal; property managers and commercial clients use their management dashboard.' },
                  { q: 'How do I view my invoices and payment history?',
                    a: 'From your customer portal — the Invoices, Payments and Statements tabs are always available.' },
                ],
              },
              {
                category: 'Sustainability',
                items: [
                  { q: 'What is NECS\'s approach to sustainability?',
                    a: 'NECS prioritizes environmentally responsible products and practices where performance, safety and operational requirements allow.' },
                  { q: 'Does NECS use environmentally responsible products?',
                    a: 'Yes — where performance and safety requirements permit. Program details are reviewed with your account manager.' },
                ],
              },
            ];
            // Inject keys so we can map tab → group
            const KEY_MAP = ['general', 'commercial', 'quote', 'contracts',
                             'payments', 'multifamily', 'technology', 'sustainability'];
            FAQ_GROUPS.forEach((g, i) => { g.key = KEY_MAP[i]; });
            const TAB_LABELS_STATIC = {
              general: 'General',
              commercial: 'Commercial',
              quote: 'Quote & Pricing',
              contracts: 'Contracts',
              payments: 'Payments',
              multifamily: 'Multifamily',
              technology: 'Technology',
              sustainability: 'Sustainability',
            };

            // Iter-92 · Locked to the 3-section static structure per CEO
            // directive. Internal knowledge FAQs are unchanged and continue
            // to power the dashboards / Noble AI.
            const GROUPS = FAQ_GROUPS;
            const TAB_LABELS = TAB_LABELS_STATIC;

            const active = GROUPS.find((g) => g.key === faqTab) || GROUPS[0];
            return (
              <div data-testid="faq-list">
                {/* Iter-150 · Red Team Fix 2 — single horizontal line
                    of FAQ topic pills (no wrap). Scrolls horizontally
                    on mobile so the row never breaks into multiple lines. */}
                <div
                  className="flex flex-nowrap overflow-x-auto justify-start sm:justify-center gap-2 sm:gap-6 border-b mb-8 pb-1"
                  style={{ borderColor: 'rgba(201,169,98,0.25)' }}
                  role="tablist"
                  aria-label="FAQ topics"
                  data-testid="faq-tabs"
                >
                  {GROUPS.map((g) => {
                    const isActive = g.key === faqTab;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => setFaqTab(g.key)}
                        className="relative whitespace-nowrap flex-shrink-0 px-3 sm:px-4 pb-3 pt-2 text-sm font-bold tracking-wider transition-colors"
                        style={{
                          fontFamily: 'Barlow Condensed, sans-serif',
                          textTransform: 'uppercase',
                          letterSpacing: '0.12em',
                          color: isActive ? '#C9A962' : '#d4d4d4',
                        }}
                        data-testid={`faq-tab-${g.key}`}
                      >
                        {TAB_LABELS[g.key]}
                        {isActive && (
                          <span
                            className="absolute left-0 right-0 bottom-[-1px] h-[3px]"
                            style={{ backgroundColor: '#C9A962' }}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Active group items */}
                <div className="space-y-3" data-testid={`faq-panel-${active.key}`}>
                  {active.items.map((item, i) => (
                    <FaqItem
                      key={`${active.key}-${i}`}
                      q={item.q}
                      a={item.a}
                      testid={`faq-${active.key}-${i}`}
                    />
                  ))}
                </div>

                {/* Iter-92 · Commercial CTA — routes prospects to the
                    live Contact Us page (not a modal). Contact Us surfaces
                    the "I am a..." selector + support@nobleempirecs.com
                    email pipeline. */}


              </div>
            );
          })()}
        </div>
      </section>

      {/* CTA Section — Contact Us + Elevating Multifamily band merged.
          Iter-152 · single section, single background image spans both
          the "Ready to Elevate Your Facility?" content and the
          "Elevating Multifamily Living..." band directly below. */}
      <section
        className="relative pt-8 pb-5 px-4 sm:px-6 lg:px-8 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundColor: '#1f1f1f',
          backgroundImage: "linear-gradient(rgba(15,15,15,0.72), rgba(15,15,15,0.82)), url('https://customer-assets-eiarnc6j.emergentagent.net/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/j6jf45lr_file_000000007d7081f58e53ec87213e32be.png')",
        }}
        data-testid="final-cta-section"
        data-views="home multifamily commercials sustainability services about mission faq residents management service-packages technology-apps"
      >
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }} data-testid="final-cta-heading">
            READY TO BUILD A BETTER <span style={{ color: '#C9A962' }}>PROPERTY-CARE PROGRAM?</span>
          </h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: '#e5e5e5', lineHeight: 1.6 }} data-testid="final-cta-description">
            Tell NECS about your property, business or facility. We'll help develop a service program based on your operational requirements, facility size and service priorities.
          </p>
          {/* Iter-215 · Get a Quote / Facility Assessment / Talk to Sales buttons removed
              from the footer CTA per CEO request to shorten the section (forms stay on their pages). */}
          {/* Iter-152 · Yellow Contact Us block — thickness reduced
              (py-3 vs py-5) and width constrained (max-w-2xl) per user
              request. */}
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-3 px-4 py-3 rounded-full max-w-2xl mx-auto mt-6 text-left"
            style={{ backgroundColor: '#C9A962' }}
            data-testid="cta-contact-us-block"
          >
            <div
              className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full"
              style={{ backgroundColor: '#000000' }}
              data-testid="cta-contact-icon"
              aria-hidden="true"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 64 64"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M16 28 C16 18, 24 12, 32 12 C40 12, 48 18, 48 28" />
                <rect x="13" y="27" width="6" height="9" rx="2" fill="#FFFFFF" />
                <rect x="45" y="27" width="6" height="9" rx="2" fill="#FFFFFF" />
                <circle cx="32" cy="32" r="9" fill="#FFFFFF" stroke="none" />
                <path d="M16 56 C16 46, 24 42, 32 42 C40 42, 48 46, 48 56" fill="#FFFFFF" stroke="none" />
              </svg>
            </div>
            <div className="min-w-0 text-center sm:text-left">
              <p
                className="text-xs sm:text-sm font-semibold"
                style={{ color: '#000000' }}
                data-testid="cta-contact-subcopy"
              >
                Need more information or assistance? We're here to help.
              </p>
            </div>
            <Button
              size="sm"
              className="font-bold text-xs sm:text-sm h-8 px-5 rounded-full hover:opacity-90 transition flex-shrink-0"
              style={{ backgroundColor: '#000000', color: '#ffffff', borderColor: '#000000' }}
              onClick={() => navigate('/contact?source=contact')}
              data-testid="cta-contact-us-btn"
            >
              Contact Us
            </Button>
          </div>

          {/* Iter-153 · Merged "Elevating Multifamily Living..." headline
              + partner sub-copy + quick-links row + company details —
              all inside the same background-image section. Footer below
              keeps only the © copyright bar. */}
          <div className="max-w-5xl mx-auto text-center mt-8" data-testid="elevating-multifamily-band">
            <h3
              className="text-base sm:text-lg lg:text-xl font-bold tracking-tight text-white leading-snug"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Elevating Multifamily Living and Commercial Space Through <span style={{ color: '#C9A962' }}>Professional Property Care</span>
            </h3>
            <p
              className="text-xs sm:text-sm max-w-3xl mx-auto mt-3"
              style={{ color: '#d6d6d6', lineHeight: 1.7 }}
              data-testid="elevating-multifamily-subcopy"
            >
              We partner with property management teams committed to excellence by providing reliable, detail-driven solutions that enhance curb appeal, improve resident satisfaction, and help protect long-term property value.
            </p>
          </div>

          {/* Iter-153 · Quick links row lifted from the footer into
              this merged hero section. */}
          <ul
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm font-semibold mt-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em' }}
            data-testid="footer-quick-links"
          >
            <li><button type="button" onClick={() => goToView('about')} className="hover:text-white transition" style={{ color: '#C9A962' }} data-testid="footer-link-about">ABOUT NECS</button></li>
            <li aria-hidden="true" className="hidden sm:inline" style={{ color: '#4a4a4a' }}>•</li>
            <li><button type="button" onClick={() => goToView('faq')} className="hover:text-white transition" style={{ color: '#C9A962' }} data-testid="footer-link-faq">FAQS</button></li>
            <li aria-hidden="true" className="hidden sm:inline" style={{ color: '#4a4a4a' }}>•</li>
            <li><Link to="/privacy" className="hover:text-white transition" style={{ color: '#C9A962' }} data-testid="footer-link-privacy">PRIVACY POLICY</Link></li>
            <li aria-hidden="true" className="hidden sm:inline" style={{ color: '#4a4a4a' }}>•</li>
            <li><Link to="/terms" className="hover:text-white transition" style={{ color: '#C9A962' }} data-testid="footer-link-terms">TERMS OF SERVICE</Link></li>
          </ul>

          {/* Iter-152 · Company details lifted into the same photo section. */}
          <div
            className="grid sm:grid-cols-3 gap-4 max-w-5xl mx-auto mt-6 pt-5 text-left"
            data-testid="footer-company-details"
          >
            <div className="text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: '#C9A962' }}>Headquarters</p>
              <p className="text-xs text-white leading-relaxed">
                2450 Lakeside Pkwy, Ste 150<br />
                Flower Mound, TX 75022
              </p>
            </div>
            <div className="text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: '#C9A962' }}>Phone</p>
              <div className="flex flex-wrap items-baseline justify-center sm:justify-start gap-x-2 gap-y-1" data-testid="footer-phone-row">
                <a href="tel:+18882413077" className="text-xs text-white whitespace-nowrap hover:text-[#C9A962] transition" data-testid="footer-phone">
                  +1 (888) 241-3077
                </a>
                <span className="text-[10px] leading-relaxed whitespace-nowrap" style={{ color: '#e5e5e5' }} data-testid="footer-phone-hours">
                  Mon–Fri · 8:30 AM – 4:30 PM CDT
                </span>
              </div>
              <p className="mt-1 text-xs leading-relaxed" data-testid="footer-fax-row">
                <span className="text-[10px] uppercase mr-2" style={{ color: '#C9A962' }} data-testid="footer-fax-label">Fax</span>
                <a href="tel:+18177669697" className="text-white hover:text-[#C9A962] transition" data-testid="footer-fax">
                  (817) 766-9697
                </a>
              </p>
            </div>
            <div className="text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: '#C9A962' }}>Email</p>
              <a href="mailto:support@nobleempirecs.com" className="text-xs text-white hover:text-[#C9A962] transition break-words" data-testid="footer-email">
                support@nobleempirecs.com
              </a>
              <p className="text-[10px] mt-1" style={{ color: '#e5e5e5' }}>Replies within 1 business day</p>
            </div>
          </div>
          {/* Iter-153 · Chat line removed per user request. */}
        </div>
      </section>

      {/* Iter-152 · In-page CTA form modals — Get a Quote + Request a
          Facility Assessment now open inline instead of routing away. */}
      <Dialog open={ctaModal === 'quote'} onOpenChange={(o) => !o && setCtaModal(null)}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: '#2D2D2D', color: '#f5f5f5', border: '1px solid #C9A962' }}
          data-testid="final-cta-quote-modal"
        >
          <DialogHeader>
            <DialogTitle
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Get a <span style={{ color: '#C9A962' }}>Quote</span>
            </DialogTitle>
          </DialogHeader>
          <MultifamilyQuoteForm />
        </DialogContent>
      </Dialog>

      <Dialog open={ctaModal === 'assessment'} onOpenChange={(o) => !o && setCtaModal(null)}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: '#2D2D2D', color: '#f5f5f5', border: '1px solid #C9A962' }}
          data-testid="final-cta-assessment-modal"
        >
          <DialogHeader>
            <DialogTitle
              className="text-2xl sm:text-3xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            >
              Request a <span style={{ color: '#C9A962' }}>Facility Assessment</span>
            </DialogTitle>
          </DialogHeader>
          <FacilityAssessmentForm />
        </DialogContent>
      </Dialog>

      {/* Footer — Iter-153 · slimmed to only the © copyright bar.
          All company info, quick links, and taglines live in the
          merged final-cta-section above. */}
      <footer id="contact" className="px-0" style={{ backgroundColor: '#000000' }} data-views="home multifamily commercials sustainability services about mission faq contact residents management service-packages technology-apps">
        {/* Copyright bar */}
        <div className="px-4 py-4 text-center text-xs" style={{ color: '#d6d6d6' }}>
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Noble Empire Cleaning Services, LLC · Dallas–Fort Worth, Texas · All rights reserved.</p>
        </div>
      </footer>

      {/* Public "Try Live Demo" lead capture modal — used by nav + PM card CTAs */}
      <LeadCaptureModal open={demoModalOpen} onOpenChange={setDemoModalOpen} />
    </div>
  );
}

function FeatureCard({ icon, title, body, testid }) {
  return (
    <div
      data-testid={testid}
      className="group bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 transition-all hover:border-[#C9A962] hover:bg-zinc-900"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 transition-colors" style={{ backgroundColor: '#C9A962', color: '#1F1F1F' }}>
        {icon}
      </div>
      <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
        {title}
      </h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl border transition-all"
      style={{ backgroundColor: '#2D2D2D', borderColor: open ? '#C9A962' : '#3D3D3D' }}
      data-testid={testid}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 p-5 text-left"
      >
        <span className="text-base font-semibold text-white flex-1" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '1.125rem' }}>
          {q}
        </span>
        <ChevronDown
          className="h-5 w-5 flex-shrink-0 mt-0.5 transition-transform"
          style={{ color: '#C9A962', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>
      {open && (
        <div
          className="px-5 pb-5 -mt-1 text-sm"
          style={{ color: '#a0a0a0', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
        >
          {a}
        </div>
      )}
    </div>
  );
}
